// Owner Portal - Full Dashboard
import React, { useState, useEffect, useCallback } from 'react';
import { fmtDate, fmtDateShort, fmtDatetime } from '../../utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.296/build/pdf.worker.min.mjs`;
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Building2, FileText, AlertCircle, DollarSign, Eye, Stethoscope, LogOut,
  ChevronDown, ChevronRight, Plus, Download, Search, Check, CheckCircle, Gavel,
  FolderOpen, Clock, Key, Upload, Image, Film, Heart, Bell, Settings, X, Mail, Send, Timer, Calendar,
  Users, ArrowLeft, CircleStar,
} from 'lucide-react';
import HorseshoeIcon from '../../components/icons/HorseshoeIcon';
import QuestionnaireCompletionDialog from '../../components/sales/QuestionnaireCompletionDialog';

const formatCurrency = (amount, currency = 'USD') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 0 }).format(amount || 0);

/* PDF viewer using react-pdf — reliable multi-page scrollable viewer on all devices */
const PDFViewer = ({ src }) => {
  const [numPages, setNumPages] = React.useState(null);
  const [error, setError] = React.useState(false);
  const containerRef = React.useRef(null);
  const [width, setWidth] = React.useState(340);

  React.useEffect(() => {
    if (containerRef.current) setWidth(containerRef.current.offsetWidth);
  }, []);

  if (error) return (
    <div className="flex flex-col items-center justify-center h-48 gap-3">
      <p className="text-sm text-muted-foreground">Could not display PDF.</p>
      <a href={src} download="report.pdf" className="text-sm underline text-primary">
        Download PDF instead
      </a>
    </div>
  );

  return (
    <div ref={containerRef}
      style={{ overflowY: 'auto', WebkitOverflowScrolling: 'touch', maxHeight: '75vh' }}
      className="w-full bg-muted/20">
      <Document
        file={src}
        onLoadSuccess={({ numPages }) => setNumPages(numPages)}
        onLoadError={(err) => { console.error('PDF load error:', err); setError(true); }}
        loading={
          <div className="flex flex-col items-center justify-center h-48 gap-2">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-xs text-muted-foreground">Loading…</p>
          </div>
        }
      >
        {numPages && Array.from({ length: numPages }, (_, i) => (
          <Page
            key={i + 1}
            pageNumber={i + 1}
            width={width}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            className="mb-1"
          />
        ))}
      </Document>
    </div>
  );
};

/* Scaled iframe preview — injects viewport meta + CSS into blob HTML content
   so it reflows to the container width. Safe for same-origin blob: URLs.    */
const ScaledIframePreview = ({ src, title }) => {
  const handleLoad = (e) => {
    try {
      const doc = e.target.contentDocument || e.target.contentWindow?.document;
      if (!doc || !doc.head) return;

      // Add viewport meta so the HTML treats the iframe width as the viewport
      if (!doc.querySelector('meta[name="viewport"]')) {
        const meta = doc.createElement('meta');
        meta.name = 'viewport';
        meta.content = 'width=device-width, initial-scale=1, maximum-scale=5';
        doc.head.appendChild(meta);
      }

      // Force all content to stay within the container width
      const style = doc.createElement('style');
      style.textContent = `
        html, body {
          max-width: 100% !important;
          overflow-x: hidden !important;
          box-sizing: border-box !important;
          margin: 0 !important;
          padding: 4px !important;
        }
        *, *::before, *::after { box-sizing: border-box !important; }
        table { width: 100% !important; max-width: 100% !important; }
        img   { max-width: 100% !important; height: auto !important; }
      `;
      doc.head.appendChild(style);
    } catch (_) { /* PDF or cross-origin — skip */ }
  };

  return (
    <div style={{
      width: '100%',
      height: '75vh',
      overflow: 'auto',
      WebkitOverflowScrolling: 'touch',
      position: 'relative',
      borderRadius: '4px',
    }}>
      <iframe
        src={src}
        title={title}
        onLoad={handleLoad}
        style={{
          display: 'block',
          border: 'none',
          width: '100%',
          height: '100%',
          minHeight: '600px',
        }}
        scrolling="yes"
      />
    </div>
  );
};

const NotesFilterSection = ({ confNotes, visibleFilters, setPdfPreview, setPdfPreviewTitle, setPdfPreviewType }) => {
  const [activeFilter, setActiveFilter] = React.useState('all');
  const BADGE_LABEL = { conformation_note: 'Conformation', vet_procedure_note: 'Vet Procedure', layup_note: 'Layup', sales_inspection_note: 'Sale Inspection', other_note: 'Other' };  const BADGE_COLOR = { conformation_note: 'bg-blue-100 text-blue-700', vet_procedure_note: 'bg-green-100 text-green-700', layup_note: 'bg-yellow-100 text-yellow-700', sales_inspection_note: 'bg-purple-100 text-purple-700', other_note: 'bg-gray-100 text-gray-600' };
  const filtered = activeFilter === 'all' ? confNotes : confNotes.filter(n => (n.note_type || 'conformation_note') === activeFilter);
  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold">Notes</h3>
      </div>
      {/* Filter pills — only show if more than one type present */}
      {visibleFilters.length > 2 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {visibleFilters.map(t => (
            <button key={t.value}
              onClick={() => setActiveFilter(t.value)}
              className={`text-[11px] px-2.5 py-1 rounded-full border font-medium transition-colors ${
                activeFilter === t.value
                  ? 'bg-primary text-white border-primary'
                  : 'bg-background text-muted-foreground border-border hover:border-primary hover:text-primary'
              }`}>
              {t.label}
            </button>
          ))}
        </div>
      )}
      <div className="space-y-2 max-h-[440px] overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
        {filtered.length === 0
          ? <p className="text-xs text-muted-foreground text-center py-4">No notes of this type.</p>
          : filtered.map(n => {
              const nt = n.note_type || 'conformation_note';
              return (
                <NoteCard key={n.id} n={n} nt={nt}
                  badgeLabel={BADGE_LABEL[nt] || nt}
                  badgeColor={BADGE_COLOR[nt] || 'bg-gray-100 text-gray-600'}
                  setPdfPreview={setPdfPreview} setPdfPreviewTitle={setPdfPreviewTitle} setPdfPreviewType={setPdfPreviewType} />
              );
            })
        }
      </div>
    </div>
  );
};

const NoteCard = ({ n, nt, badgeLabel, badgeColor, setPdfPreview, setPdfPreviewTitle, setPdfPreviewType }) => {
  const [photoOpen, setPhotoOpen] = React.useState(false);
  const photoSrc = n.picture_url ? (n.picture_url.startsWith('http') ? n.picture_url : `${window.location.origin}${n.picture_url}`) : null;
  return (
    <div className="p-3 border rounded text-sm">
      <div className="flex justify-between items-start gap-2 mb-1">
        <p className="font-medium">{n.title}</p>
        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 ${badgeColor}`}>{badgeLabel}</span>
      </div>
      {/* Date + grade badge */}
      <div className="flex justify-between items-center text-xs text-muted-foreground mb-1">
        <span>{n.date ? fmtDate(n.date) : ''}{n.by ? ` · by ${n.by}` : ''}</span>
        {nt === 'conformation_note' && n.grade && (
          <Badge variant="outline" className="text-xs">{n.grade}</Badge>
        )}
      </div>
      {n.notes && (
        <p className="text-muted-foreground text-xs"><span className="font-medium text-foreground">Notes: </span>{n.notes}</p>
      )}
      {/* Photo — collapsible row matching Photos & Files style */}
      {photoSrc && (
        <div className="mt-2">
          <button
            className="flex items-center justify-between w-full px-2 py-1.5 border rounded hover:bg-muted/50 text-xs"
            onClick={() => setPhotoOpen(o => !o)}
          >
            <div className="flex items-center gap-2">
              <Image className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">Photo</span>
            </div>
            <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${photoOpen ? 'rotate-90' : ''}`} />
          </button>
          {photoOpen && (
            <img src={photoSrc} alt="Note" className="rounded border max-h-48 object-contain w-full cursor-pointer mt-2"
              onClick={() => { setPdfPreviewType('image'); setPdfPreviewTitle(n.title || 'Photo'); setPdfPreview(photoSrc); }} />
          )}
        </div>
      )}
      {/* File */}
      {n.file_id && (
        <div className="mt-2">
          <button
            className="flex items-center justify-between w-full px-2 py-1.5 border rounded hover:bg-muted/50 text-xs"
            onClick={async () => {
              const token = localStorage.getItem('token');
              const res = await fetch(`${window.location.origin}${n.file_url}`, { headers: { Authorization: `Bearer ${token}` } });
              if (!res.ok) { toast.error('Could not load file'); return; }
              const blob = await res.blob();
              const url = URL.createObjectURL(blob);
              setPdfPreviewTitle(n.file_name || 'File');
              setPdfPreviewType(blob.type.startsWith('image/') ? 'image' : 'pdf');
              setPdfPreview(url);
            }}
          >
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">{n.file_name || 'File'}</span>
            </div>
            <Eye className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      )}
    </div>
  );
};

const CARE_FILTER_STYLES = {
  'All':          { active: 'bg-primary text-white border-primary',          inactive: 'bg-white text-gray-500 border-gray-200' },
  'Medical':      { active: 'bg-purple-100 text-purple-700 border-purple-300',    inactive: 'bg-purple-50 text-purple-500 border-purple-200' },
  'Daily Care':   { active: 'bg-sky-100 text-sky-700 border-sky-300',             inactive: 'bg-sky-50 text-sky-500 border-sky-200' },
  'Maintenance':  { active: 'bg-yellow-100 text-yellow-700 border-yellow-300',    inactive: 'bg-yellow-50 text-yellow-600 border-yellow-200' },
  'Conditioning': { active: 'bg-emerald-100 text-emerald-700 border-emerald-300', inactive: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
  'Travel':       { active: 'bg-indigo-100 text-indigo-700 border-indigo-300',    inactive: 'bg-indigo-50 text-indigo-500 border-indigo-200' },
};

const CareUpdatesSection = ({ farmfolioCare }) => {
  const [activeCat, setActiveCat] = React.useState('All');
  const ALL_CATS = ['All','Medical','Daily Care','Maintenance','Conditioning','Travel'];
  const presentCats = new Set(farmfolioCare.map(e => e.category));
  const visibleCats = ALL_CATS.filter(c => c === 'All' || presentCats.has(c));
  const filtered = activeCat === 'All' ? farmfolioCare : farmfolioCare.filter(e => e.category === activeCat);
  return (
    <div className="border rounded-lg p-4">
      <h3 className="text-sm font-semibold mb-3">Care Updates</h3>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {visibleCats.map(c => (
          <button key={c} onClick={() => setActiveCat(c)}
            className={`px-2.5 py-0.5 rounded-full text-xs font-medium border transition-colors ${
              activeCat === c ? (CARE_FILTER_STYLES[c]?.active || '') : (CARE_FILTER_STYLES[c]?.inactive || '')
            }`}>{c}</button>
        ))}
      </div>
      <div className="space-y-2">
        {filtered.map(entry => (
          <div key={entry.id} className="p-3 border rounded bg-muted/20">
            <p className="text-sm font-medium">{entry.type} — {entry.name}</p>
            {entry.dosage && <p className="text-xs text-muted-foreground">{entry.dosage} {entry.dosage_unit || ''}</p>}
            <p className="text-xs text-muted-foreground mt-0.5">
              {entry.is_layup
                ? `${entry.log_date && fmtDate(entry.log_date + 'T12:00:00')}${entry.end_date ? ' → ' + fmtDate(entry.end_date + 'T12:00:00') : ' → Open-ended'}`
                : entry.log_date && fmtDate(entry.log_date + 'T12:00:00')}
              {entry.performed_by && ` · ${entry.performed_by}`}
              {entry.location_name && ` · ${entry.location_name}`}
            </p>
            {entry.comments && <p className="text-xs mt-1 text-muted-foreground italic">{entry.comments}</p>}
          </div>
        ))}
      </div>
    </div>
  );
};

const OwnerPortalPage = () => {
  const navigate = useNavigate();
  const { user, logout, userTimezone } = useAuth();
  const qc = useQueryClient();

  // ── Server state (9 useQuery calls, one per portal data section) ──────────
  const { data: portalDashboard, isLoading: loading } = useQuery({
    queryKey: QK.ownerPortalDashboard(),
    queryFn: () => api.get('/owner-portal/dashboard').then(r => r.data),
    onError: () => toast.error('Failed to load portal'),
  });
  const data = portalDashboard;

  const { data: buyerAcks = [] } = useQuery({
    queryKey: QK.ownerPortalBuyerAcks(),
    queryFn: () => api.get('/owner-portal/buyer-acknowledgments').then(r => r.data),
  });

  const { data: portalQuestionnaires = [] } = useQuery({
    queryKey: QK.ownerPortalQuestionnaires(),
    queryFn: () => api.get('/owner-portal/questionnaires').then(r => r.data),
  });

  const { data: docsToSign = [] } = useQuery({
    queryKey: QK.ownerPortalDocs(),
    queryFn: () => api.get('/owner-portal/documents-to-sign').then(r => r.data),
  });

  const { data: publishedReports = [] } = useQuery({
    queryKey: QK.ownerPortalReports(),
    queryFn: () => api.get('/owner-portal/published-reports').then(r => r.data),
  });

  const { data: portalSyndicates = [] } = useQuery({
    queryKey: QK.ownerPortalSyndicates(),
    queryFn: () => api.get('/owner-portal/syndicates').then(r => r.data),
  });

  // Portal alerts — with unread count derived from data
  const { data: alertsData = [] } = useQuery({
    queryKey: QK.ownerPortalAlerts(),
    queryFn: () => api.get('/owner-portal/alerts').then(r => r.data),
  });
  const alerts        = Array.isArray(alertsData) ? alertsData : (alertsData?.alerts || []);
  const unreadCount   = Array.isArray(alertsData) ? alertsData.filter(a => !a.read).length : (alertsData?.unread_count || 0);

  // Final reports — lazy (only when section is opened)
  const [finalReportsRequested, setFinalReportsRequested] = useState(false);
  const { data: finalReports = [], isLoading: finalReportsLoading } = useQuery({
    queryKey: QK.ownerPortalFinalReports(),
    queryFn: () => api.get('/owner-portal/final-sale-reports').then(r => r.data),
    enabled: finalReportsRequested,
  });

  // Invalidation helpers
  const invalidateDashboard        = () => qc.invalidateQueries({ queryKey: QK.ownerPortalDashboard() });
  const invalidateAlerts           = () => qc.invalidateQueries({ queryKey: QK.ownerPortalAlerts() });
  const invalidateQuestionnaires   = () => qc.invalidateQueries({ queryKey: QK.ownerPortalQuestionnaires() });

  // ── UI state ──────────────────────────────────────────────────────────────
  const [activeSection, setActiveSection] = useState('dashboard');
  const [linkDialog, setLinkDialog] = useState(false);
  const [linkKey, setLinkKey] = useState('');
  const [linking, setLinking] = useState(false);
  const [linkKeyResult, setLinkKeyResult] = useState(null);
  const [consignorDetail, setConsignorDetail] = useState(null);
  const [horseDetail, setHorseDetail] = useState(null);
  const [horseDetailLoading, setHorseDetailLoading] = useState(false);
  const [horseFilePreview, setHorseFilePreview] = useState(null);
  const [horseFileBlobUrls, setHorseFileBlobUrls] = useState({});
  const [horseFileMimeTypes, setHorseFileMimeTypes] = useState({});
  const [horseFileTextContent, setHorseFileTextContent] = useState({});
  const [saleOppDetail, setSaleOppDetail] = useState(null);
  const [saleOppLoading, setSaleOppLoading] = useState(false);
  const [saleOppMeta, setSaleOppMeta] = useState(null);
  const [saleOppBreezeTime, setSaleOppBreezeTime] = useState(null);
  const [finalReportDetail, setFinalReportDetail] = useState(null);
  const [finalSearch, setFinalSearch] = useState('');
  const [reportFilePreview, setReportFilePreview] = useState(null);
  const [reportFileBlobUrls, setReportFileBlobUrls] = useState({});
  const [docSearch, setDocSearch] = useState('');
  const [actionsOpen, setActionsOpen] = useState(true);
  const [invoicesOpen, setInvoicesOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const [questOpen, setQuestOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [buyerAckOpen, setBuyerAckOpen] = useState(false);
  const [completingQuestionnaire, setCompletingQuestionnaire] = useState(null);
  const [uploadingQuestionnaire, setUploadingQuestionnaire] = useState(null);
  const questUploadRef = React.useRef(null);
  const [presaleOpen, setPresaleOpen] = useState(true);
  const [liveOpen, setLiveOpen] = useState(true);
  const [presaleSearch, setPresaleSearch] = useState('');
  const [liveSearch, setLiveSearch] = useState('');

  // Syndicates (extra detail — lazy on click)
  const [activeSyndicate, setActiveSyndicate] = useState(null);
  const [activeSyndicateDetail, setActiveSyndicateDetail] = useState(null);
  const [syndicateHorses, setSyndicateHorses] = useState([]);
  const [syndicateFiles, setSyndicateFiles] = useState([]);
  const [syndicateHorsesLoading, setSyndicateHorsesLoading] = useState(false);

  const handleQuestionnaireUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadingQuestionnaire) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('sale_owner_deliverable_id', uploadingQuestionnaire);
      formData.append('description', 'Uploaded Completed Questionnaire');
      formData.append('category', 'questionnaire_completed');
      await api.post('/files/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Questionnaire uploaded successfully');
      invalidateQuestionnaires();
    } catch {
      toast.error('Failed to upload questionnaire');
    } finally {
      setUploadingQuestionnaire(null);
      if (questUploadRef.current) questUploadRef.current.value = '';
    }
  };

  const openSyndicateDetail = async (syn) => {
    setActiveSyndicate(syn);
    setActiveSyndicateDetail(null);
    setSyndicateHorsesLoading(true);
    setSyndicateHorses([]);
    setSyndicateFiles([]);
    try {
      const [detailRes, horsesRes, filesRes] = await Promise.all([
        api.get(`/owner-portal/syndicates/${syn.id}`),
        api.get(`/owner-portal/syndicates/${syn.id}/horses`),
        api.get(`/owner-portal/syndicates/${syn.id}/files`),
      ]);
      setActiveSyndicateDetail(detailRes.data);
      setSyndicateHorses(horsesRes.data || []);
      setSyndicateFiles(filesRes.data || []);
    } catch {
      toast.error('Failed to load syndicate details');
    } finally {
      setSyndicateHorsesLoading(false);
    }
  };

  const dismissAlert = async (alertId) => {
    try {
      await api.post(`/owner-portal/alerts/${alertId}/dismiss`);
      invalidateAlerts();
    } catch { toast.error('Failed to dismiss'); }
  };

  const openSettings = async () => {
    setSettingsOpen(true);
    try {
      const res = await api.get('/owner-portal/settings');
      setSettingsData(res.data);
    } catch { /* ignore */ }
  };

  const saveSettings = async () => {
    try {
      await api.put('/owner-portal/settings', settingsData);
      toast.success('Preferences saved');
    } catch { toast.error('Failed to save'); }
  };

  const changePassword = async () => {
    if (pwNew !== pwConfirm) { toast.error('Passwords do not match'); return; }
    try {
      await api.post('/owner-portal/change-password', { current_password: pwCurrent, new_password: pwNew });
      toast.success('Password changed');
      setPwCurrent(''); setPwNew(''); setPwConfirm('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to change password'); }
  };

  const toggleAlertPref = (key, field) => {
    setSettingsData(prev => ({
      ...prev,
      alert_preferences: {
        ...prev.alert_preferences,
        [key]: { ...prev.alert_preferences[key], [field]: field === 'enabled' ? !prev.alert_preferences[key].enabled : prev.alert_preferences[key][field] }
      }
    }));
  };

  const toggleAlertMethod = (key, method) => {
    setSettingsData(prev => {
      const methods = prev.alert_preferences[key]?.methods || [];
      const newMethods = methods.includes(method) ? methods.filter(m => m !== method) : [...methods, method];
      return {
        ...prev,
        alert_preferences: {
          ...prev.alert_preferences,
          [key]: { ...prev.alert_preferences[key], methods: newMethods }
        }
      };
    });
  };

  const openMessages = async () => {
    setMessagesOpen(true);
    setAlertsOpen(false);
    setSettingsOpen(false);
    try {
      const [convosRes, usersRes] = await Promise.all([
        api.get('/messages/conversations'),
        api.get('/messages/tenant-users'),
      ]);
      setConversations(convosRes.data || []);
      setTenantUsers(usersRes.data || []);
    } catch { /* ignore */ }
  };

  const openConversation = async (convoId) => {
    try {
      const res = await api.get(`/messages/conversations/${convoId}`);
      setActiveConvo(res.data.conversation);
      setConvoMessages(res.data.messages || []);
      qc.invalidateQueries({ queryKey: QK.ownerPortalUnreadCount() });
    } catch { toast.error('Failed to load conversation'); }
  };

  const startNewConversation = async (recipientId) => {
    try {
      const res = await api.post('/messages/conversations', { recipient_id: recipientId });
      setActiveConvo(res.data);
      setConvoMessages([]);
      setNewMsgRecipient(null);
      const convosRes = await api.get('/messages/conversations');
      setConversations(convosRes.data || []);
    } catch { toast.error('Failed to start conversation'); }
  };

  const sendMessage = async () => {
    if (!messageText.trim() || !activeConvo) return;
    try {
      const res = await api.post('/messages/send', { conversation_id: activeConvo.id, text: messageText.trim() });
      setConvoMessages(prev => [...prev, res.data]);
      setMessageText('');
      const convosRes = await api.get('/messages/conversations');
      setConversations(convosRes.data || []);
    } catch { toast.error('Failed to send'); }
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  // Impersonation banner state
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonationData, setImpersonationData] = useState(null);

  useEffect(() => {
    const impersonationId = localStorage.getItem('impersonation_id');
    const originalUser = localStorage.getItem('original_user');
    if (impersonationId && originalUser) {
      setIsImpersonating(true);
      setImpersonationData({ impersonationId, originalUser: JSON.parse(originalUser) });
    }
  }, []);

  const handleExitImpersonation = async () => {
    try {
      const impersonationId = localStorage.getItem('impersonation_id');
      const originalToken = localStorage.getItem('original_token') || localStorage.getItem('token');
      const originalUser = localStorage.getItem('original_user');
      if (!impersonationId || !originalUser) { toast.error('Impersonation data not found'); return; }
      const endRes = await fetch(`${window.location.origin}/api/impersonation/end/${impersonationId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${originalToken}`, 'Content-Type': 'application/json' }
      });
      if (!endRes.ok) throw new Error('Server failed to end impersonation session');
      localStorage.setItem('user', originalUser);
      localStorage.setItem('token', originalToken);
      localStorage.removeItem('impersonation_id');
      localStorage.removeItem('original_user');
      localStorage.removeItem('original_token');
      toast.success('Exited impersonation mode');
      window.location.href = '/admin/system';
    } catch (error) {
      toast.error('Failed to exit impersonation');
    }
  };

  // Build a full URL for consignor logo images stored as relative paths
  const makeLogoUrl = (logoUrl) => {
    if (!logoUrl) return null;
    // /api/files/download/ requires auth — swap for the public (no-auth) endpoint
    const url = logoUrl.replace('/api/files/download/', '/api/files/public/');
    if (url.startsWith('http')) return url;
    const base = window.location.origin || '';
    const path = url.startsWith('/') ? url : `/${url}`;
    return `${base}${path}`;
  };

  // fmtDate / fmtDatetime / fmtDateShort imported from dateUtils — replaces local fmtBirthdate
  // fmtDate handles all stored formats safely (no UTC day-off bug)

  const handleLinkConsignor = async () => {
    if (!linkKey.trim()) return;
    setLinking(true);
    try {
      await api.post('/owner-portal/link-consignor', { access_key: linkKey.trim() });
      toast.success('Access requested. Waiting for Connection approval.');
      setLinkDialog(false);
      setLinkKey('');
      setLinkKeyResult(null);
      invalidateDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link');
    }
    setLinking(false);
  };

  const handleMarkCompleted = async (invoiceId) => {
    try {
      await api.post('/owner-portal/mark-completed', { invoice_id: invoiceId });
      toast.success('Marked as completed');
      invalidateDashboard();
    } catch {
      toast.error('Failed to mark as completed');
    }
  };

  const openHorseDetail = async (horseId) => {
    setHorseDetailLoading(true);
    setHorseDetail(null);
    try {
      const [res, careRes] = await Promise.all([
        api.get(`/owner-portal/horse/${horseId}`),
        api.get(`/farmfolio/owner-portal/horse/${horseId}/care`).catch(() => ({ data: [] })),
      ]);
      setHorseDetail({ ...res.data, farmfolio_care: careRes.data || [] });
    } catch {
      toast.error('Failed to load horse details');
    }
    setHorseDetailLoading(false);
  };

  const [pdfPreview, setPdfPreview] = useState(null);
  const [pdfPreviewTitle, setPdfPreviewTitle] = useState('Document Preview');
  const [pdfPreviewType, setPdfPreviewType] = useState('pdf'); // 'pdf' | 'image'
  const [pdfLoading, setPdfLoading] = useState(false);

  const openPdfPreview = async (fetchFn, title = 'Document Preview') => {
    setPdfPreviewTitle(title);
    setPdfPreview('loading');   // opens dialog immediately
    setPdfLoading(true);
    try {
      const blob = await fetchFn();
      const url = URL.createObjectURL(blob);
      setPdfPreview(url);
    } catch {
      setPdfPreview(null);
      toast.error('Failed to load document');
    } finally {
      setPdfLoading(false);
    }
  };

  const downloadBlob = (blob, filename) => {
    const u = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = u; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(u), 100);
  };

  const fetchPortalFile = async (fileId) => {
    const token = localStorage.getItem('token');
    const r = await fetch(`${window.location.origin}/api/owner-portal/file-download/${fileId}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) throw new Error('Failed');
    return r.blob();
  };

  const fetchReportPdf = async (savedReportId) => {
    const res = await api.get(`/owner-portal/published-reports/${savedReportId}/pdf`, { responseType: 'blob' });
    return new Blob([res.data], { type: 'application/pdf' });
  };
  // alerts and unreadCount come from the ownerPortalAlerts useQuery above
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsData, setSettingsData] = useState(null);
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [messagesOpen, setMessagesOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [convoMessages, setConvoMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [tenantUsers, setTenantUsers] = useState([]);
  const [newMsgRecipient, setNewMsgRecipient] = useState(null);
  // unreadMsgCount — derived from messaging state (UI only, not server-cached)
  const [unreadMsgCount, setUnreadMsgCount] = useState(0);

  const openInvoicePdf = async (invoiceId) => {
    setPdfPreviewTitle('Invoice');
    setPdfPreview('loading');
    setPdfLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${window.location.origin}/api/owner-portal/invoice/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      setPdfPreview(URL.createObjectURL(blob));
    } catch {
      setPdfPreview(null);
      toast.error('Failed to load PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const openFinalReportDetail = async (report) => {
    setFinalReportDetail(null);
    setReportFilePreview(null);
    setReportFileBlobUrls({});
    setSaleOppMeta(report);
    setSaleOppLoading(true);
    try {
      const res = await api.get(`/owner-portal/sale-opportunity/${report.id}`);
      setFinalReportDetail({ ...res.data, ...report });
    } catch { toast.error('Failed to load report'); }
    setSaleOppLoading(false);
  };

  const openSaleOppDetail = async (opp) => {
    setSaleOppMeta(opp);
    setSaleOppLoading(true);
    setSaleOppDetail(null);
    setSaleOppBreezeTime(null);
    try {
      const res = await api.get(`/owner-portal/sale-opportunity/${opp.id}`);
      setSaleOppDetail(res.data);
      // Fetch breeze time if undertack show
      if (opp.is_undertack_show) {
        try {
          const bt = await api.get(`/breeze-times/${opp.id}`);
          setSaleOppBreezeTime(bt.data || null);
        } catch { /* ignore */ }
      }
    } catch {
      toast.error('Failed to load sale details');
    }
    setSaleOppLoading(false);
  };

  const openHorseFile = async (fileId) => {
    if (horseFilePreview === fileId) { setHorseFilePreview(null); return; }
    setHorseFilePreview(fileId);
    if (!horseFileBlobUrls[fileId]) {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${window.location.origin}/api/owner-portal/file/${fileId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const blob = await res.blob();
          const actualMime = blob.type || '';
          setHorseFileMimeTypes(prev => ({ ...prev, [fileId]: actualMime }));
          if (actualMime.startsWith('text/')) {
            const text = await blob.text();
            setHorseFileTextContent(prev => ({ ...prev, [fileId]: text }));
          }
          setHorseFileBlobUrls(prev => ({ ...prev, [fileId]: URL.createObjectURL(blob) }));
        }
      } catch { /* ignore */ }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  const {
    consignors = [], entities = [], horses = [], ownership_records = [],
    pending_invoices = [], presale_opportunities = [], live_opportunities = [],
    all_invoices = [], action_required_count = 0, has_payment_processing = false, pending_access_count = 0,
  } = data || {};

  return (
    <div className="min-h-screen bg-secondary flex flex-col" data-testid="owner-portal">
      {/* Impersonation Banner */}
      {isImpersonating && impersonationData && (
        <div className="bg-amber-500 text-white px-4 py-2 flex items-center justify-between gap-3 z-[60] sticky top-0" data-testid="impersonation-banner">
          <span className="text-sm font-medium">
            Impersonating: <strong>{user?.first_name} {user?.last_name}</strong> ({user?.email})
            &nbsp;·&nbsp; Original: {impersonationData.originalUser?.email}
          </span>
          <Button
            size="sm"
            variant="secondary"
            className="h-7 text-xs bg-white text-amber-700 hover:bg-amber-50 shrink-0"
            onClick={handleExitImpersonation}
            data-testid="exit-impersonation-btn"
          >
            Stop Impersonating
          </Button>
        </div>
      )}
      {/* Top Ribbon */}
      <header className="bg-primary px-4 py-2 sm:px-6 sm:py-3 sticky top-0 z-50">
        {/* Mobile: two rows — Desktop: single row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => setActiveSection('dashboard')}>
            <img src="/logo.svg" alt="Green Door" className="w-8 h-8 sm:w-10 sm:h-10 object-contain brightness-0 invert" />
            <span className="hidden sm:inline text-white font-serif text-lg font-semibold">Green Door</span>
          </div>
          <span className="text-white/90 text-sm font-medium sm:hidden">{user?.first_name} {user?.last_name}</span>
          <div className="flex items-center gap-1 sm:gap-3">
            <span className="hidden sm:inline text-white/80 text-sm">{user?.first_name} {user?.last_name}</span>
            <div className="relative">
              <Button variant="ghost" size="sm" className="text-white hover:bg-white/10 h-8 w-8 p-0" onClick={() => { setAlertsOpen(!alertsOpen); setSettingsOpen(false); }} data-testid="alerts-btn">
                <Bell className="w-4 h-4" />
              </Button>
              {alerts.length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center font-medium">{alerts.length}</span>
              )}
            </div>
            <div className="relative">
              <Button variant="ghost" size="sm" className="text-white hover:bg-white/10 h-8 w-8 p-0" onClick={() => { openMessages(); }} data-testid="messages-btn">
                <Mail className="w-4 h-4" />
              </Button>
              {unreadMsgCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center font-medium">{unreadMsgCount}</span>
              )}
            </div>
            <Button variant="ghost" size="sm" className="text-white hover:bg-white/10 h-8 w-8 p-0" onClick={() => { openSettings(); setAlertsOpen(false); }} data-testid="settings-btn">
              <Settings className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" className="text-white hover:bg-white/10 h-8 w-8 sm:w-auto sm:px-2 p-0" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Sign Out</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Alerts Dropdown */}
      {alertsOpen && (
        <div className="absolute right-6 top-14 z-50 w-96 bg-white border rounded-lg shadow-lg">
          <div className="p-3 border-b flex items-center justify-between">
            <p className="text-sm font-semibold">Alerts</p>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setAlertsOpen(false)}><X className="w-4 h-4" /></Button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {alerts.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No alerts</p>
            ) : (
              alerts.map(a => (
                <div key={a.id}
                  className={`flex items-start gap-3 p-3 border-b hover:bg-muted/30 transition-colors ${a.horse_id ? 'cursor-pointer' : ''}`}
                  onClick={() => {
                    if (a.horse_id) {
                      setAlertsOpen(false);
                      openHorseDetail(a.horse_id);
                    }
                  }}
                >
                  <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${a.type === 'sale_result' ? 'bg-green-500' : a.type === 'vet_procedure' ? 'bg-blue-500' : 'bg-yellow-500'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">{a.message}</p>
                    {a.horse_id && <p className="text-[10px] text-primary font-medium mt-0.5">Tap to view horse</p>}
                    <p className="text-[10px] text-muted-foreground">{a.created_at ? fmtDate(a.created_at) : ''}</p>
                  </div>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0" onClick={(e) => { e.stopPropagation(); dismissAlert(a.id); }}>
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-2">
            {/* Change Password */}
            <div>
              <h3 className="text-sm font-semibold mb-3">Change Password</h3>
              <div className="space-y-2">
                <Input type="password" placeholder="Current password" value={pwCurrent} onChange={(e) => setPwCurrent(e.target.value)} />
                <Input type="password" placeholder="New password" value={pwNew} onChange={(e) => setPwNew(e.target.value)} />
                <Input type="password" placeholder="Confirm new password" value={pwConfirm} onChange={(e) => setPwConfirm(e.target.value)} />
                <Button size="sm" onClick={changePassword} disabled={!pwCurrent || !pwNew || !pwConfirm} className="bg-primary hover:bg-primary/90 text-white">
                  Update Password
                </Button>
              </div>
            </div>

            <hr className="border-border" />

            {/* Alert Preferences */}
            {settingsData?.alert_preferences && (() => {
              const SALE_ALERTS = [
                { key: 'repeat_view',                    label: 'Repeat View',          desc: 'When a horse gets a repeat viewing' },
                { key: 'vet_procedure_all',              label: 'All Vet Procedures',   desc: 'Any vetting procedure is scheduled or completed' },
                { key: 'vet_procedure_heart_scan',       label: 'Heart Scan',           desc: 'Heart scan scheduled or completed' },
                { key: 'vet_procedure_measurement',      label: 'Measurement',          desc: 'Measurement scheduled or completed' },
                { key: 'vet_procedure_physical',         label: 'Physical',             desc: 'Physical examination scheduled or completed' },
                { key: 'vet_procedure_scope_in_person',  label: 'Scope - In Person',    desc: 'In-person scope scheduled or completed' },
                { key: 'vet_procedure_scope_repository', label: 'Scope - Repository',   desc: 'Repository scope scheduled or completed' },
                { key: 'vet_procedure_ultrasound',       label: 'Ultrasound',           desc: 'Ultrasound scheduled or completed' },
                { key: 'vet_procedure_xray_in_person',   label: 'X-ray - In Person',    desc: 'In-person X-ray scheduled or completed' },
                { key: 'vet_procedure_xray_repository',  label: 'X-ray - Repository',   desc: 'Repository X-ray scheduled or completed' },
                { key: 'vet_procedure_other',            label: 'Other Procedure',      desc: 'Any other vetting procedure' },
                { key: 'sale_result',                    label: 'Sale Result',          desc: 'When a horse is sold, RNA, or withdrawn' },
                { key: 'final_sale_report',              label: 'Final Sale Report',    desc: 'When a Final Sale Report is published to your portal' },
              ];
              const FARMFOLIO_ALERTS = [
                { key: 'ff_layup',       label: 'Layup',       desc: 'When one of your horses is placed on layup' },
                { key: 'ff_medication',  label: 'Medication',  desc: 'When a medication is logged for one of your horses' },
                { key: 'ff_vaccination', label: 'Vaccination', desc: 'When a vaccination is logged' },
                { key: 'ff_procedure',   label: 'Procedure',   desc: 'When a medical procedure is logged' },
                { key: 'ff_travel',      label: 'Travel',      desc: 'When a travel event is logged' },
                { key: 'ff_bred',        label: 'Bred',        desc: 'When a cover record is logged' },
                { key: 'ff_pregnant',    label: 'Pregnant',    desc: 'When your mare is confirmed pregnant' },
                { key: 'ff_foaled',      label: 'Foaled',      desc: 'When a delivery milestone is recorded' },
              ];
              const SYSTEM_ALERTS = [
                { key: 'new_action_required',      label: 'New Action Required',      desc: 'When an action item requires your attention' },
                { key: 'document_vault_additions', label: 'Document Vault Additions', desc: 'When a new document is added to your vault' },
                { key: 'horse_updates',            label: 'Horse Updates',            desc: 'When a note, photo, or file is added to one of your horses' },
              ];
              const renderAlertRow = ({ key, label, desc }) => {
                const pref = settingsData.alert_preferences[key] || { enabled: false, methods: ['portal'] };
                return (
                  <div key={key} className="border rounded p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-sm font-medium">{label}</p>
                        <p className="text-[10px] text-muted-foreground">{desc}</p>
                      </div>
                      <Button
                        variant={pref.enabled ? 'default' : 'outline'}
                        size="sm"
                        className={`h-7 text-xs ${pref.enabled ? 'bg-primary hover:bg-primary/90 text-white' : ''}`}
                        onClick={() => toggleAlertPref(key, 'enabled')}
                      >
                        {pref.enabled ? 'On' : 'Off'}
                      </Button>
                    </div>
                    {pref.enabled && (
                      <div className="flex gap-2 mt-1">
                        {['portal', 'email', 'text'].map(method => (
                          <Button
                            key={method}
                            variant={pref.methods?.includes(method) ? 'default' : 'outline'}
                            size="sm"
                            className={`h-6 text-[10px] capitalize ${pref.methods?.includes(method) ? 'bg-primary hover:bg-primary/90 text-white' : ''}`}
                            onClick={() => toggleAlertMethod(key, method)}
                          >
                            {method === 'portal' ? 'Portal' : method === 'email' ? 'Email' : 'Push Notification'}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              };
              return (
                <div className="space-y-5">
                  <div>
                    <h3 className="text-sm font-semibold mb-3">Sale Alerts</h3>
                    <div className="space-y-3">
                      {SALE_ALERTS.map(renderAlertRow)}
                    </div>
                  </div>
                  {consignors.some(c => c.is_farm) && (
                  <div>
                    <h3 className="text-sm font-semibold mb-3">FarmFolio Alerts</h3>
                    <div className="space-y-3">
                      {FARMFOLIO_ALERTS.map(renderAlertRow)}
                    </div>
                  </div>
                  )}
                  <div>
                    <h3 className="text-sm font-semibold mb-3">System Alerts</h3>
                    <div className="space-y-3">
                      {SYSTEM_ALERTS.map(renderAlertRow)}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>Cancel</Button>
            <Button onClick={saveSettings} className="bg-primary hover:bg-primary/90 text-white">Save Preferences</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Messages Dialog */}
      <Dialog open={messagesOpen} onOpenChange={(v) => { setMessagesOpen(v); if (!v) { setActiveConvo(null); setNewMsgRecipient(null); } }}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh]" style={{ maxWidth: 'min(42rem, calc(100vw - 1rem))' }}>
          <DialogHeader>
            <DialogTitle>Messages</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col sm:flex-row h-[500px] border rounded overflow-hidden">
            {/* Conversation List — full-width on mobile when no convo open */}
            <div className={`sm:w-48 sm:border-r sm:shrink-0 flex flex-col ${activeConvo || newMsgRecipient ? 'hidden sm:flex' : 'flex'}`}>
              <div className="p-2 border-b">
                <Button size="sm" className="w-full text-xs bg-primary hover:bg-primary/90 text-white" onClick={() => setNewMsgRecipient('selecting')}>
                  <Plus className="w-3 h-3 mr-1" /> New Message
                </Button>
              </div>
              <div className="flex-1 overflow-y-auto">
                {conversations.map(c => {
                  const other = c.participant_details?.find(p => p.id !== user?.id);
                  return (
                    <div
                      key={c.id}
                      className={`p-3 cursor-pointer hover:bg-muted/50 border-b ${activeConvo?.id === c.id ? 'bg-muted' : ''}`}
                      onClick={() => openConversation(c.id)}
                    >
                      <p className="text-sm font-medium truncate">{other?.name || 'Unknown'}</p>
                      <p className="text-xs text-muted-foreground truncate">{c.last_message || 'No messages'}</p>
                      {c.unread_count > 0 && (
                        <span className="w-4 h-4 rounded-full bg-red-500 text-white text-[9px] inline-flex items-center justify-center font-medium mt-1">{c.unread_count}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Chat Area — full-width on mobile when convo open */}
            <div className={`flex-1 flex flex-col min-w-0 ${activeConvo || newMsgRecipient ? 'flex' : 'hidden sm:flex'}`}>
              {newMsgRecipient === 'selecting' ? (
                <div className="p-4 space-y-2">
                  <div className="flex items-center gap-2 mb-3">
                    <Button variant="ghost" size="sm" className="sm:hidden h-8 px-2" onClick={() => setNewMsgRecipient(null)}>
                      ← Back
                    </Button>
                    <p className="text-sm font-medium">Select recipient:</p>
                  </div>
                  {tenantUsers.map(u => (
                    <div
                      key={u.id}
                      className="flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-muted/30"
                      onClick={() => startNewConversation(u.id)}
                    >
                      <div className="flex-1">
                        <p className="text-sm font-medium">{u.name}</p>
                        <p className="text-[10px] text-muted-foreground">{u.tenant_name} • {u.email}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : activeConvo ? (
                <>
                  <div className="p-2 border-b bg-muted/20 flex items-center gap-2">
                    <Button variant="ghost" size="sm" className="sm:hidden h-8 px-2 shrink-0" onClick={() => { setActiveConvo(null); setConvoMessages([]); }}>
                      ← Back
                    </Button>
                    <p className="text-sm font-medium truncate">
                      {activeConvo.participant_details?.find(p => p.id !== user?.id)?.name ||
                       conversations.find(c => c.id === activeConvo.id)?.participant_details?.find(p => p.id !== user?.id)?.name || 'Conversation'}
                    </p>
                  </div>
                  <div className="flex-1 overflow-y-auto p-3 space-y-2">
                    {convoMessages.map(m => (
                      <div key={m.id} className={`flex ${m.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] p-2 rounded-lg text-sm ${m.sender_id === user?.id ? 'bg-primary text-white' : 'bg-muted'}`}>
                          <p>{m.text}</p>
                          <p className={`text-[9px] mt-1 ${m.sender_id === user?.id ? 'text-white/60' : 'text-muted-foreground'}`}>
                            {m.created_at ? new Date(m.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : ''}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="p-2 border-t flex gap-2">
                    <Input
                      placeholder="Type a message..."
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') sendMessage(); }}
                      className="h-9 text-sm"
                    />
                    <Button size="sm" onClick={sendMessage} disabled={!messageText.trim()} className="bg-primary hover:bg-primary/90 text-white h-9 shrink-0">
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <p className="text-sm text-muted-foreground">Select a conversation or start a new one</p>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <main className="flex-1">
        <div className="px-6 py-6 space-y-6">

          {/* Dashboard Section */}
          {activeSection === 'dashboard' && (
            <>
              {/* Header */}
              <div>
                <h1 className="font-serif text-3xl font-semibold">Hello, {user?.first_name}</h1>
                <p className="text-muted-foreground mt-1">Here is your dashboard</p>
                {pending_access_count > 0 && (
                  <p className="text-sm text-yellow-600 mt-1">
                    <Clock className="w-3.5 h-3.5 inline mr-1" />
                    {pending_access_count} Business access request(s) pending approval
                  </p>
                )}
              </div>
              {/* My Consignors + Navigation Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 sm:items-stretch">
                <Card className="flex-1">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="font-serif text-lg">My Connections</CardTitle>
                      <Button size="sm" variant="outline" onClick={() => setLinkDialog(true)} data-testid="link-consignor-btn">
                        <Plus className="w-4 h-4 mr-1" /> Link Connection
                      </Button>
                    </div>
                    <div className="h-1 bg-primary rounded mt-2" />
                  </CardHeader>
                  <CardContent>
                  {consignors.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No connections linked yet. Click "Link Connection" to get started.</p>
                  ) : (
                    <div className="flex flex-wrap gap-4">
                      {consignors.map((c) => (
                        <div
                          key={c.id}
                          className="flex flex-col items-center gap-2 p-4 border rounded hover:bg-muted/30 cursor-pointer transition-colors w-[140px] h-[140px] justify-center"
                          onClick={() => setConsignorDetail(c)}
                          data-testid={`consignor-${c.id}`}
                        >
                          {c.logo_url ? (
                            <img src={makeLogoUrl(c.logo_url)} alt={c.name} className="w-12 h-12 object-contain"
                              onError={e => { e.currentTarget.style.display = 'none'; }} />
                          ) : (
                            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                              <Building2 className="w-6 h-6 text-primary" />
                            </div>
                          )}
                          <span className="text-xs font-medium text-center">{c.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
                </Card>
                <div className="flex flex-col gap-3 sm:shrink-0 sm:w-56">
                  <Card
                    className="cursor-pointer hover:shadow-md transition-shadow flex"
                    onClick={() => { setActiveSection('sale-reports'); setFinalReportsRequested(true); }}
                    data-testid="nav-sale-reports"
                  >
                    <CardContent className="p-4 flex items-center gap-3 w-full">
                      <FileText className="w-6 h-6 text-primary shrink-0" />
                      <span className="text-sm font-medium">Final Sale Reports</span>
                    </CardContent>
                  </Card>
                  <Card
                    className="cursor-pointer hover:shadow-md transition-shadow flex"
                    onClick={() => setActiveSection('stable')}
                    data-testid="nav-stable"
                  >
                    <CardContent className="p-4 flex items-center gap-3 w-full">
                      <HorseshoeIcon className="w-6 h-6 text-primary shrink-0" />
                      <span className="text-sm font-medium">My Horses</span>
                    </CardContent>
                  </Card>
                  {portalSyndicates.length > 0 && (
                    <Card
                      className="cursor-pointer hover:shadow-md transition-shadow flex"
                      onClick={() => { setActiveSection('syndicates'); setActiveSyndicate(null); setSyndicateHorses([]); }}
                      data-testid="nav-syndicates"
                    >
                      <CardContent className="p-4 flex items-center gap-3 w-full">
                        <Users className="w-6 h-6 text-primary shrink-0" />
                        <span className="text-sm font-medium">My Syndicates</span>
                      </CardContent>
                    </Card>
                  )}
                  <Card
                    className="cursor-pointer hover:shadow-md transition-shadow flex"
                    onClick={() => setActiveSection('documents')}
                    data-testid="nav-documents"
                  >
                    <CardContent className="p-4 flex items-center gap-3 w-full">
                      <FolderOpen className="w-6 h-6 text-primary shrink-0" />
                      <span className="text-sm font-medium">Document Vault</span>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Action Required - inline (only if items exist) */}
              {(pending_invoices.length > 0 || true /* remove 'true' when real docs/quests exist */) && (
              <Card className="border-l-4 border-l-red-500">
                <CardHeader className="pb-3">
                  <CardTitle className="font-serif text-lg flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                    Action Required
                  </CardTitle>
                  <div className="h-1 bg-primary rounded mt-2" />
                </CardHeader>
                <CardContent className="space-y-1">
                  {/* Unpaid Invoices */}
                  <div className="border rounded">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center p-3 cursor-pointer hover:bg-muted/30 gap-2" onClick={() => setInvoicesOpen(!invoicesOpen)}>
                      <div className="flex items-center gap-2">
                        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${invoicesOpen ? '' : '-rotate-90'}`} />
                        <DollarSign className="w-4 h-4 text-foreground" />
                        <p className="text-sm font-semibold">Unpaid Invoices</p>
                        {pending_invoices.length > 0 && <span className="w-5 h-5 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-medium shrink-0">{pending_invoices.length}</span>}
                      </div>
                      {invoicesOpen && pending_invoices.length > 0 && (
                        <div className="flex gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
                          <Button variant="outline" size="sm" className="text-xs flex-1 sm:flex-none">
                            <Download className="w-3 h-3 mr-1" /> Download All
                          </Button>
                          {has_payment_processing && (
                            <Button size="sm" className="text-xs bg-primary hover:bg-primary/90 text-white flex-1 sm:flex-none" onClick={() => toast.info('Payment processing coming soon')}>
                              <DollarSign className="w-3 h-3 mr-1" /> Pay All Outstanding
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                    {invoicesOpen && pending_invoices.length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-4 px-3">No unpaid invoices</p>
                    )}
                    {invoicesOpen && pending_invoices.length > 0 && (
                      <div className="px-3 pb-3">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Consignor</TableHead>
                              <TableHead>Invoice</TableHead>
                              <TableHead>Due Date</TableHead>
                              <TableHead>Amount</TableHead>
                              <TableHead>Urgency</TableHead>
                              <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {pending_invoices.map((inv) => {
                              const consignor = consignors.find(c => entities.find(e => e.id === inv.crm_entity_id && e.tenant_id === c.id));
                              return (
                                <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                                  <TableCell className="text-sm">{consignor?.name || '-'}</TableCell>
                                  <TableCell className="text-sm font-medium">#{inv.invoice_number || inv.id?.slice(0, 8)}</TableCell>
                                  <TableCell className="text-sm">{inv.invoice_due_date ? fmtDate(inv.invoice_due_date) : '-'}</TableCell>
                                  <TableCell className="text-sm font-medium">{formatCurrency(inv.display_amount || inv.owner_portion_total || inv.total_amount)}</TableCell>
                                  <TableCell>
                                    <Badge className={
                                      inv.urgency === 'overdue' ? 'bg-red-100 text-red-700' :
                                      inv.urgency === 'due_soon' ? 'bg-yellow-100 text-yellow-700' :
                                      'bg-gray-100 text-gray-700'
                                    }>
                                      {inv.urgency === 'overdue' ? 'Overdue' : inv.urgency === 'due_soon' ? 'Due Soon' : 'Unpaid'}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <div className="flex gap-1 justify-end">
                                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="View" onClick={() => openInvoicePdf(inv.id)}>
                                        <Eye className="w-3.5 h-3.5" />
                                      </Button>
                                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Download">
                                        <Download className="w-3.5 h-3.5" />
                                      </Button>
                                      {consignor?.feature_flags?.payment_processing && (
                                        <Button size="sm" className="h-7 text-xs bg-primary hover:bg-primary/90 text-white" onClick={() => toast.info('Payment processing coming soon')}>
                                          <DollarSign className="w-3 h-3 mr-1" /> Pay Now
                                        </Button>
                                      )}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>

                  {/* Documents to Sign / Upload */}
                  <div className="border rounded">
                    <div className="flex justify-between items-center p-3 cursor-pointer hover:bg-muted/30" onClick={() => setDocsOpen(!docsOpen)}>
                      <div className="flex items-center gap-2">
                        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${docsOpen ? '' : '-rotate-90'}`} />
                        <FileText className="w-4 h-4 text-foreground" />
                        <p className="text-sm font-semibold">Documents to Sign / Upload</p>
                        {docsToSign.filter(d => !d.executed_at).length > 0 && (
                          <span className="w-5 h-5 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-medium">
                            {docsToSign.filter(d => !d.executed_at).length}
                          </span>
                        )}
                      </div>
                    </div>
                    {docsOpen && (
                      <div className="px-3 pb-3">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Consignor</TableHead>
                              <TableHead>Sale</TableHead>
                              <TableHead>Document Type</TableHead>
                              <TableHead>Deadline</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {docsToSign.filter(d => !d.executed_at).length === 0 ? (
                              <TableRow><TableCell colSpan={6} className="text-sm text-muted-foreground text-center py-4">No documents awaiting signature</TableCell></TableRow>
                            ) : (
                              docsToSign.filter(d => !d.executed_at).map(doc => (
                                <TableRow key={doc.deliverable_id}>
                                  <TableCell className="text-sm">{doc.consignor_name || '—'}</TableCell>
                                  <TableCell className="text-sm">{doc.sale_name || '—'}</TableCell>
                                  <TableCell className="text-sm font-medium">{doc.doc_label || 'Sale Authorization'}</TableCell>
                                  <TableCell className="text-sm">
                                    {doc.deadline ? fmtDate(doc.deadline) : '—'}
                                  </TableCell>
                                  <TableCell>
                                    <Badge className="bg-yellow-100 text-yellow-700 pointer-events-none">Awaiting Signature</Badge>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <div className="flex gap-1 justify-end">
                                      {doc.authorization_file_id && (
                                        <>
                                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="View"
                                            onClick={() => {
                                              const token = localStorage.getItem('token');
                                              fetch(`${window.location.origin}/api/owner-portal/file-download/${doc.authorization_file_id}`, { headers: { Authorization: `Bearer ${token}` } })
                                                .then(r => { if (!r.ok) throw new Error('Failed'); return r.blob(); })
                                                .then(blob => { const u = URL.createObjectURL(blob); setPdfPreviewTitle(doc.doc_label || 'Authorization'); setPdfPreview(u); setPdfLoading(false); })
                                                .catch(() => toast.error('Failed to load file'));
                                            }}>
                                            <Eye className="w-3.5 h-3.5" />
                                          </Button>
                                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Download"
                                            onClick={() => {
                                              const token = localStorage.getItem('token');
                                              fetch(`${window.location.origin}/api/owner-portal/file-download/${doc.authorization_file_id}`, { headers: { Authorization: `Bearer ${token}` } })
                                                .then(r => { if (!r.ok) throw new Error('Failed'); return r.blob(); })
                                                .then(blob => { const u = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = u; a.download = doc.authorization_filename || 'authorization.pdf'; a.click(); URL.revokeObjectURL(u); })
                                                .catch(() => toast.error('Failed to download file'));
                                            }}>
                                            <Download className="w-3.5 h-3.5" />
                                          </Button>
                                        </>
                                      )}
                                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Upload signed copy">
                                        <Upload className="w-3.5 h-3.5" />
                                      </Button>
                                      <Button size="sm" className="h-7 text-xs bg-primary hover:bg-primary/90 text-white"
                                        onClick={() => window.location.href = doc.deliverable_type === 'consignment_contract' ? `/sign-authorization/${doc.deliverable_id}` : `/sign-authorization/${doc.deliverable_id}`}>
                                        Sign
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>

                  {/* Questionnaires to Complete */}
                  <div className="border rounded">
                    <div className="flex justify-between items-center p-3 cursor-pointer hover:bg-muted/30" onClick={() => setQuestOpen(!questOpen)}>
                      <div className="flex items-center gap-2">
                        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${questOpen ? '' : '-rotate-90'}`} />
                        <FileText className="w-4 h-4 text-foreground" />
                        <p className="text-sm font-semibold">Questionnaires to Complete</p>
                        {portalQuestionnaires.filter(q => q.status !== 'completed').length > 0 && (
                          <span className="w-5 h-5 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-medium">
                            {portalQuestionnaires.filter(q => q.status !== 'completed').length}
                          </span>
                        )}
                      </div>
                    </div>
                    {questOpen && (
                      <div className="px-3 pb-3">
                        {portalQuestionnaires.filter(q => q.status !== 'completed').length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-4">No questionnaires to complete</p>
                        ) : (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Consignor</TableHead>
                                <TableHead>Sale</TableHead>
                                <TableHead>Horse</TableHead>
                                <TableHead>Hip #</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {portalQuestionnaires.filter(q => q.status !== 'completed').map(q => (
                                <TableRow key={q.deliverable_id}>
                                  <TableCell className="text-sm">{q.consignor_name || '—'}</TableCell>
                                  <TableCell className="text-sm">{q.sale_name || '—'}</TableCell>
                                  <TableCell className="text-sm font-medium">{q.horse_name || '—'}</TableCell>
                                  <TableCell className="text-sm">#{q.hip_number}</TableCell>
                                  <TableCell>
                                    <Badge className="bg-yellow-100 text-yellow-700 pointer-events-none">To Complete</Badge>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <div className="flex gap-1 justify-end">
                                      {q.questionnaire_file_id && (
                                        <>
                                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="View"
                                            onClick={() => {
                                              const token = localStorage.getItem('token');
                                              fetch(`${window.location.origin}/api/owner-portal/file-download/${q.questionnaire_file_id}`, { headers: { Authorization: `Bearer ${token}` } })
                                                .then(r => r.blob()).then(blob => { const u = URL.createObjectURL(blob); setPdfPreviewTitle(q.horse_name || 'Questionnaire'); setPdfPreview(u); setPdfLoading(false); });
                                            }}>
                                            <Eye className="w-3.5 h-3.5" />
                                          </Button>
                                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Download"
                                            onClick={() => {
                                              const token = localStorage.getItem('token');
                                              fetch(`${window.location.origin}/api/owner-portal/file-download/${q.questionnaire_file_id}`, { headers: { Authorization: `Bearer ${token}` } })
                                                .then(r => r.blob()).then(blob => { const u = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = u; a.download = q.questionnaire_filename || 'questionnaire.pdf'; a.click(); });
                                            }}>
                                            <Download className="w-3.5 h-3.5" />
                                          </Button>
                                        </>
                                      )}
                                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Upload Completed Questionnaire"
                                        data-testid={`upload-questionnaire-${q.deliverable_id}`}
                                        onClick={() => {
                                          setUploadingQuestionnaire(q.deliverable_id);
                                          questUploadRef.current?.click();
                                        }}>
                                        <Upload className="w-3.5 h-3.5" />
                                      </Button>
                                      <Button size="sm" className="h-7 text-xs bg-primary hover:bg-primary/90 text-white"
                                        onClick={() => setCompletingQuestionnaire(q.deliverable_id)}>
                                        Complete
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Buyer Acknowledgments */}
                  {buyerAcks.length > 0 && (
                  <div className="border rounded">
                    <div className="flex justify-between items-center p-3 cursor-pointer hover:bg-muted/30" onClick={() => setBuyerAckOpen(!buyerAckOpen)}>
                      <div className="flex items-center gap-2">
                        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${buyerAckOpen ? '' : '-rotate-90'}`} />
                        <CheckCircle className="w-4 h-4 text-foreground" />
                        <p className="text-sm font-semibold">Buyer Acknowledgments</p>
                        {buyerAcks.filter(a => !a.signed_off).length > 0 && (
                          <span className="w-5 h-5 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-medium">
                            {buyerAcks.filter(a => !a.signed_off).length}
                          </span>
                        )}
                      </div>
                    </div>
                    {buyerAckOpen && (
                      <div className="px-3 pb-3">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Consignor</TableHead>
                              <TableHead>Sale</TableHead>
                              <TableHead>Horse</TableHead>
                              <TableHead>Hip #</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {buyerAcks.map(ack => (
                              <TableRow key={ack.token}>
                                <TableCell className="text-sm">{ack.consignor_name || '—'}</TableCell>
                                <TableCell className="text-sm">{ack.sale_name || '—'}</TableCell>
                                <TableCell className="text-sm font-medium">{ack.horse_name || '—'}</TableCell>
                                <TableCell className="text-sm">#{ack.hip_number}</TableCell>
                                <TableCell>
                                  {ack.signed_off ? (
                                    <Badge className="bg-green-100 text-green-700 pointer-events-none">Signed Off</Badge>
                                  ) : (
                                    <Badge className="bg-yellow-100 text-yellow-700 pointer-events-none">Pending</Badge>
                                  )}
                                </TableCell>
                                <TableCell className="text-right">
                                  <Button
                                    size="sm"
                                    className="h-7 text-xs bg-primary hover:bg-primary/90 text-white"
                                    onClick={() => window.open(`/handover/${ack.token}`, '_blank')}
                                  >
                                    {ack.signed_off ? 'View' : 'Sign Off'}
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                  )}
                </CardContent>
              </Card>
              )}

              {/* Pre-Sale Activity */}
              {presale_opportunities.length > 0 && (
              <Card>
                <CardHeader className="cursor-pointer" onClick={() => setPresaleOpen(!presaleOpen)}>
                  <CardTitle className="font-serif text-lg flex items-center gap-2">
                    <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${presaleOpen ? '' : '-rotate-90'}`} />
                    Pre-Sale Activity
                  </CardTitle>
                  <div className="h-1 bg-primary rounded mt-2" />
                  {presaleOpen && (
                    <div className="relative max-w-xs mt-2" onClick={(e) => e.stopPropagation()}>
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input placeholder="Search pre-sale..." value={presaleSearch} onChange={(e) => setPresaleSearch(e.target.value)} className="pl-9 h-8 text-sm" />
                    </div>
                  )}
                </CardHeader>
                {presaleOpen && (
                <CardContent className="px-4 py-0 pb-4">
                  {(() => {
                    const q = presaleSearch.toLowerCase();
                    const filtered = presale_opportunities.filter(opp => {
                      if (!q) return true;
                      const stageBadgesMap = { presale_create: 'Potential Sale Entry', presale_confirm: 'Confirmed Sale Entry', presale_deliverables: 'Tracking Deliverables', presale_ready: 'Ready for Sale' };
                      return [opp.consignor_name, opp.sale_name, opp.sale_company, opp.horse_name, opp.opportunity_name, stageBadgesMap[opp.stage] || opp.stage].some(v => (v || '').toLowerCase().includes(q));
                    });
                    return filtered.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No pre-sale activity</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Consignor</TableHead>
                          <TableHead>Sale</TableHead>
                          <TableHead>Horse</TableHead>
                          <TableHead>Hip #</TableHead>
                          <TableHead>Session Date</TableHead>
                          <TableHead>Ship-in Date / Time</TableHead>
                          <TableHead>Barn</TableHead>
                          <TableHead>Pre-Sale Stage</TableHead>
                          <TableHead>Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filtered.map((opp) => {
                          const stageBadges = {
                            presale_create: { label: 'Potential Sale Entry', cls: 'bg-gray-100 text-gray-700' },
                            presale_confirm: { label: 'Confirmed Sale Entry', cls: 'bg-blue-100 text-blue-700' },
                            presale_deliverables: { label: 'Tracking Deliverables', cls: 'bg-yellow-100 text-yellow-700' },
                            presale_ready: { label: 'Ready for Sale', cls: 'bg-green-100 text-green-700' },
                          };
                          const sb = stageBadges[opp.stage] || { label: opp.stage?.replace(/_/g, ' ') || '-', cls: 'bg-gray-100 text-gray-700' };
                          // Count action items for this opportunity (contracts, questionnaires, invoices)
                          const oppActions = (opp.pending_actions || 0);
                          return (
                          <TableRow key={opp.id}>
                            <TableCell className="text-sm">{opp.consignor_name || '-'}</TableCell>
                            <TableCell className="text-sm">
                              <div>
                                <p className="font-medium">{opp.sale_name || '-'}</p>
                                <p className="text-xs text-muted-foreground">{[opp.sale_company, opp.sale_year].filter(Boolean).join(' • ')}</p>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm font-medium">{opp.horse_name || opp.opportunity_name || '-'}</TableCell>
                            <TableCell className="text-sm">#{opp.hip_number || 'TBD'}</TableCell>
                            <TableCell className="text-sm">{opp.session_date ? fmtDate(opp.session_date) : '-'}</TableCell>
                            <TableCell className="text-sm">{opp.ship_in_date ? `${fmtDate(opp.ship_in_date)}${opp.session_time ? ' ' + opp.session_time : ''}` : '-'}</TableCell>
                            <TableCell className="text-sm">{opp.barn || '-'}</TableCell>
                            <TableCell>
                              <Badge className={`text-xs pointer-events-none ${sb.cls}`}>{sb.label}</Badge>
                            </TableCell>
                            <TableCell>
                              {oppActions > 0 && (
                                <span className="w-5 h-5 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-medium">{oppActions}</span>
                              )}
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  );
                  })()}
                </CardContent>
                )}
              </Card>
              )}

              {/* Live Sale Activity */}
              {live_opportunities.length > 0 && (
              <Card>
                <CardHeader className="cursor-pointer" onClick={() => setLiveOpen(!liveOpen)}>
                  <CardTitle className="font-serif text-lg flex items-center gap-2">
                    <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${liveOpen ? '' : '-rotate-90'}`} />
                    Live Sale Activity
                  </CardTitle>
                  <div className="h-1 bg-primary rounded mt-2" />
                  {liveOpen && (
                    <div className="relative max-w-xs mt-2" onClick={(e) => e.stopPropagation()}>
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input placeholder="Search live sales..." value={liveSearch} onChange={(e) => setLiveSearch(e.target.value)} className="pl-9 h-8 text-sm" />
                    </div>
                  )}
                </CardHeader>
                {liveOpen && (
                <CardContent className="px-4 py-0 pb-4">
                  {(() => {
                    const q = liveSearch.toLowerCase();
                    const filtered = live_opportunities.filter(opp => !q || [opp.horse_name, opp.opportunity_name, opp.sale_name, opp.sale_company, opp.consignor_name, opp.hip_number?.toString(), opp.result, opp.horse_age, opp.horse_gender, opp.horse_color].some(v => (v || '').toLowerCase().includes(q)));
                    return filtered.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No matching results</p>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {filtered.map((opp) => (
                        <Card key={opp.id} className="overflow-hidden hover:shadow-md transition-shadow cursor-pointer" onClick={() => openSaleOppDetail(opp)}>
                          <div className="aspect-[4/3] bg-muted relative">
                            {opp.horse_photo_url ? (
                              <img src={opp.horse_photo_url} alt={opp.horse_name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <HorseshoeIcon className="w-8 h-8 text-muted-foreground/20" />
                              </div>
                            )}
                            <div className="absolute top-2 right-2">
                              {opp.result === 'sold' ? (
                                <Badge className="bg-green-100 text-green-700 pointer-events-none">Sold</Badge>
                              ) : opp.result === 'rna' ? (
                                <Badge className="bg-red-100 text-red-700 pointer-events-none">RNA</Badge>
                              ) : opp.result === 'withdrawn' ? (
                                <Badge className="bg-gray-100 text-gray-700 pointer-events-none">Withdrawn</Badge>
                              ) : (
                                <Badge className="bg-yellow-100 text-yellow-700 pointer-events-none">Pending</Badge>
                              )}
                            </div>
                          </div>
                          <CardContent className="p-3 space-y-1.5">
                            <h3 className="font-serif text-base font-semibold">{opp.horse_name || opp.opportunity_name || '-'}</h3>
                            <p className="text-xs text-muted-foreground">
                              {[opp.horse_age, opp.horse_gender, opp.horse_color?.replace(/_/g, ' ')].filter(Boolean).join(' • ')}
                            </p>
                            <div className="text-xs space-y-1">
                              <div>
                                <p className="font-medium">{opp.sale_name || '-'}</p>
                                <p className="text-muted-foreground">{[opp.sale_company, opp.sale_year].filter(Boolean).join(' • ')}</p>
                              </div>
                              <p className="text-muted-foreground">Consignor: {opp.consignor_name || '-'}</p>
                              <p className="text-muted-foreground">Hip #{opp.hip_number || 'TBD'}</p>
                            </div>
                            {opp.reserve_price > 0 && (
                              <div className="text-xs pt-1.5 border-t">
                                <span className="text-muted-foreground">Reserve: </span>
                                <span className="font-medium">{formatCurrency(opp.reserve_price)}</span>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  );
                  })()}
                </CardContent>
                )}
              </Card>
              )}
            </>
          )}

          {/* Final Sale Reports Section */}
          {activeSection === 'sale-reports' && (
            <Card>
              <CardHeader>
                <CardTitle className="font-serif text-lg">Final Sale Reports</CardTitle>
                <div className="h-1 bg-primary rounded mt-2" />
                <div className="relative max-w-xs mt-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input placeholder="Search reports..." value={finalSearch} onChange={(e) => setFinalSearch(e.target.value)} className="pl-9 h-8 text-sm" />
                </div>
              </CardHeader>
              <CardContent className="px-4 py-0 pb-4">
                {finalReportsLoading ? (
                  <div className="flex justify-center py-8"><Skeleton className="h-8 w-48" /></div>
                ) : (() => {
                  const q = finalSearch.toLowerCase();
                  const filtered = finalReports.filter(r => !q || [r.horse_name, r.sale_name, r.sale_company, r.consignor_name, r.hip_number?.toString(), r.result].some(v => (v || '').toLowerCase().includes(q)));
                  return filtered.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No final sale reports published yet</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Consignor</TableHead>
                          <TableHead>Sale</TableHead>
                          <TableHead>Horse</TableHead>
                          <TableHead>Hip #</TableHead>
                          <TableHead>Result</TableHead>
                          <TableHead>Sale Price</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filtered.map((r) => (
                          <TableRow key={r.id} className="cursor-pointer hover:bg-muted/50" onClick={() => openFinalReportDetail(r)}>
                            <TableCell className="text-sm">{r.consignor_name || '-'}</TableCell>
                            <TableCell className="text-sm">
                              <div>
                                <p className="font-medium">{r.sale_name || '-'}</p>
                                <p className="text-xs text-muted-foreground">{[r.sale_company, r.sale_year].filter(Boolean).join(' • ')}</p>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm font-medium">{r.horse_name || '-'}</TableCell>
                            <TableCell className="text-sm">#{r.hip_number || 'TBD'}</TableCell>
                            <TableCell>
                              {r.result === 'sold' ? <Badge className="bg-green-100 text-green-700 pointer-events-none">Sold</Badge> :
                               r.result === 'rna' ? <Badge className="bg-red-100 text-red-700 pointer-events-none">RNA</Badge> :
                               r.result === 'withdrawn' ? <Badge className="bg-gray-100 text-gray-700 pointer-events-none">Withdrawn</Badge> :
                               <Badge className="bg-yellow-100 text-yellow-700 pointer-events-none">Pending</Badge>}
                            </TableCell>
                            <TableCell className="text-sm font-medium">{r.sale_price ? formatCurrency(r.sale_price) : '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          {/* My Horses Section */}
          {activeSection === 'stable' && (
            <Card>
              <CardHeader>
                <CardTitle className="font-serif text-lg">My Horses</CardTitle>
                <div className="h-1 bg-primary rounded mt-2" />
              </CardHeader>
              <CardContent>
                {ownership_records.length === 0 ? (
                  <div className="text-center py-8">
                    <HorseshoeIcon className="w-12 h-12 mx-auto mb-3 text-muted-foreground/20" />
                    <p className="text-muted-foreground">No horses in your portfolio</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {ownership_records.map((record, idx) => {
                      const horse = horses.find(h => h.id === record.horse_id);
                      if (!horse) return null;
                      const isSyndicateHorse = record?.is_syndicate_horse;
                      const entityName = entities.find(e => e.id === record?.entity_id)?.system_name;
                      const consignor = consignors.find(c => {
                        const entity = entities.find(e => e.id === record?.entity_id);
                        return entity && c.id === entity.tenant_id;
                      });
                      return (
                        <Card key={`${horse.id}-${idx}`} className="overflow-hidden cursor-pointer hover:shadow-md transition-shadow" data-testid={`horse-card-${horse.id}-${idx}`} onClick={() => openHorseDetail(horse.id)}>
                          <div className="aspect-[4/3] bg-muted relative">
                            {horse.main_photo_url ? (
                              <img src={horse.main_photo_url} alt={horse.system_name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <HorseshoeIcon className="w-8 h-8 text-muted-foreground/20" />
                              </div>
                            )}
                          </div>
                          <CardContent className="p-3">
                            <h3 className="font-serif text-sm font-semibold">{horse.system_name}</h3>
                            <p className="text-xs text-muted-foreground">
                              {[horse.age, horse.gender, horse.color?.replace(/_/g, ' ')].filter(Boolean).join(' • ')}
                            </p>
                            {record && (
                              <div className="mt-1.5 p-1.5 rounded text-xs" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
                                <p className="font-medium">
                                  Ownership: {Number(record.ownership_percent).toFixed(2)}%
                                  {isSyndicateHorse && record.syndicate_ownership_percent != null && (
                                    <span className="font-normal text-muted-foreground ml-1">
                                      ({record.user_syndicate_percent}% of {record.syndicate_ownership_percent}%)
                                    </span>
                                  )}
                                </p>
                                {isSyndicateHorse && record.syndicate_name
                                  ? <p className="text-muted-foreground">via {record.syndicate_name}</p>
                                  : entityName && <p className="text-muted-foreground">via {entityName}</p>
                                }
                              </div>
                            )}
                            {consignor && (
                              <p className="text-xs text-muted-foreground mt-1.5">Business: {consignor.name}</p>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* My Syndicates Section */}
          {activeSection === 'syndicates' && (
            <div className="space-y-4">
              {/* Header */}
              <div className="flex items-center gap-3">
                {activeSyndicate && (
                  <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0"
                    onClick={() => { setActiveSyndicate(null); setActiveSyndicateDetail(null); setSyndicateHorses([]); setSyndicateFiles([]); }}
                    data-testid="syndicate-back-btn">
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                )}
                <div>
                  <h2 className="font-serif text-xl font-semibold">
                    {activeSyndicate ? activeSyndicate.system_name : 'My Syndicates'}
                  </h2>
                  {activeSyndicate && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      My ownership: <span className="font-medium text-foreground">{activeSyndicate.my_ownership_percent?.toFixed(1)}%</span>
                    </p>
                  )}
                </div>
              </div>

              {!activeSyndicate ? (
                /* Syndicate grid */
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {portalSyndicates.map(syn => (
                    <Card key={syn.id}
                      className="cursor-pointer hover:shadow-md transition-shadow"
                      data-testid={`syndicate-card-${syn.id}`}
                      onClick={() => openSyndicateDetail(syn)}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            {syn.profile_photo_url ? (
                              <img src={syn.profile_photo_url} alt={syn.system_name} className="w-10 h-10 rounded-full object-cover" />
                            ) : (
                              <CircleStar className="w-5 h-5 text-primary" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-serif font-semibold truncate">{syn.system_name}</h3>
                            <p className="text-xs text-muted-foreground mt-0.5">My Ownership: {syn.my_ownership_percent?.toFixed(1)}%</p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : syndicateHorsesLoading ? (
                <div className="flex justify-center py-12">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                /* Detail: 3 sections */
                <div className="space-y-6">
                  {/* (i) Ownership */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Syndicate Members</CardTitle>
                      <div className="h-0.5 bg-primary rounded mt-1" />
                    </CardHeader>
                    <CardContent className="p-0">
                      {(activeSyndicateDetail?.ownership_records || []).length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-6">No members</p>
                      ) : (
                        <div className="divide-y">
                          {(activeSyndicateDetail?.ownership_records || [])
                            .sort((a, b) => (b.ownership_percent || 0) - (a.ownership_percent || 0))
                            .map(rec => (
                              <div key={rec.id} className="px-4 py-3 text-sm font-medium">
                                {rec.owner_name || rec.owner_crm_entity_id}
                              </div>
                            ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* (ii) Photos & Files */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Photos & Files</CardTitle>
                      <div className="h-0.5 bg-primary rounded mt-1" />
                    </CardHeader>
                    <CardContent>
                      {syndicateFiles.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-6">No files published yet</p>
                      ) : (
                        <div className="space-y-2">
                          {syndicateFiles.map(f => (
                            <div key={f.id} className="flex items-center justify-between p-2 border rounded text-sm hover:bg-muted/20">
                              <span className="truncate">{f.original_filename || f.filename || 'File'}</span>
                              <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" asChild>
                                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/owner-portal/file/${f.id}`} target="_blank" rel="noopener noreferrer">
                                  <Download className="w-3.5 h-3.5" />
                                </a>
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* (iii) Horses */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Horses</CardTitle>
                      <div className="h-0.5 bg-primary rounded mt-1" />
                    </CardHeader>
                    <CardContent>
                      {syndicateHorses.length === 0 ? (
                        <div className="text-center py-8">
                          <HorseshoeIcon className="w-10 h-10 mx-auto mb-3 text-muted-foreground/20" />
                          <p className="text-muted-foreground text-sm">No horses linked to this syndicate yet</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                          {syndicateHorses.map(horse => (
                            <Card key={horse.id}
                              className="overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                              data-testid={`syndicate-horse-card-${horse.id}`}
                              onClick={() => openHorseDetail(horse.id)}>
                              <div className="aspect-[4/3] bg-muted relative">
                                {horse.main_photo_url ? (
                                  <img src={horse.main_photo_url} alt={horse.system_name} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <HorseshoeIcon className="w-8 h-8 text-muted-foreground/20" />
                                  </div>
                                )}
                              </div>
                              <CardContent className="p-3">
                                <h3 className="font-serif text-sm font-semibold">{horse.system_name}</h3>
                                <p className="text-xs text-muted-foreground">
                                  {[horse.age, horse.gender, horse.color?.replace(/_/g, ' ')].filter(Boolean).join(' • ')}
                                </p>
                                <div className="mt-1.5 p-1.5 rounded text-xs" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
                                  <p className="font-medium">Syndicate: {horse.syndicate_ownership_percent}%</p>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          )}

          {/* Document Vault Section */}
          {activeSection === 'documents' && (
            <Card>
              <CardHeader>
                <CardTitle className="font-serif text-lg flex items-center gap-2">
                  <FolderOpen className="w-5 h-5" />
                  Document Vault
                </CardTitle>
                <div className="h-1 bg-primary rounded mt-2" />
                <div className="relative max-w-xs mt-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input placeholder="Search all documents..." value={docSearch} onChange={(e) => setDocSearch(e.target.value)} className="pl-9 h-8 text-sm" />
                </div>
              </CardHeader>
              <CardContent className="space-y-1">
                {/* All Invoices */}
                <div className="border rounded">
                  <div className="flex justify-between items-center p-3 cursor-pointer hover:bg-muted/30" onClick={() => setInvoicesOpen(prev => !prev)}>
                    <div className="flex items-center gap-2">
                      <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${invoicesOpen ? '' : '-rotate-90'}`} />
                      <DollarSign className="w-4 h-4 text-primary" />
                      <p className="text-sm font-semibold">All Invoices</p>
                      <span className="text-xs text-muted-foreground">({all_invoices.length})</span>
                    </div>
                    {invoicesOpen && all_invoices.length > 0 && (
                      <Button variant="outline" size="sm" className="text-xs" onClick={(e) => e.stopPropagation()}>
                        <Download className="w-3 h-3 mr-1" /> Download All
                      </Button>
                    )}
                  </div>
                  {invoicesOpen && (() => {
                    const q = docSearch.toLowerCase();
                    const filtered = all_invoices.filter(i => !q || [i.invoice_number, i.other_notes_terms, i.crm_entity_name, i.id].some(v => (v || '').toLowerCase().includes(q)));
                    return filtered.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4 px-3">No invoices found</p>
                    ) : (
                      <div className="px-3 pb-3">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Consignor</TableHead>
                              <TableHead>Invoice</TableHead>
                              <TableHead>Due Date</TableHead>
                              <TableHead>Amount</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {filtered.map((inv) => {
                              const consignor = consignors.find(c => entities.find(e => e.id === inv.crm_entity_id && e.tenant_id === c.id));
                              return (
                              <TableRow key={inv.id}>
                                <TableCell className="text-sm">{consignor?.name || '-'}</TableCell>
                                <TableCell className="text-sm font-medium">#{inv.invoice_number || inv.id?.slice(0, 8)}</TableCell>
                                <TableCell className="text-sm">{inv.invoice_due_date ? fmtDate(inv.invoice_due_date) : '-'}</TableCell>
                                <TableCell className="text-sm font-medium">{formatCurrency(inv.display_amount || inv.owner_portion_total || inv.total_amount)}</TableCell>
                                <TableCell>
                                  <Badge className={inv.payment_status === 'paid' ? 'bg-green-100 text-green-700 pointer-events-none' : 'bg-yellow-100 text-yellow-700 pointer-events-none'}>
                                    {inv.payment_status === 'paid' ? 'Paid' : inv.amount_type === 'payable' ? 'Payable' : 'Unpaid'}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex gap-1 justify-end">
                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="View" onClick={() => openInvoicePdf(inv.id)}>
                                      <Eye className="w-3.5 h-3.5" />
                                    </Button>
                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Download" onClick={() => openInvoicePdf(inv.id)}>
                                      <Download className="w-3.5 h-3.5" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    );
                  })()}
                </div>

                {/* Executed Documents */}
                {(() => {
                  const executedDocs = docsToSign.filter(d =>
                    !!d.executed_at &&
                    (d.deliverable_type !== 'consignment_contract' || !!d.executed_file_id)
                  );
                  return (
                  <div className="border rounded">
                    <div className="flex justify-between items-center p-3 cursor-pointer hover:bg-muted/30" onClick={() => setDocsOpen(prev => !prev)}>
                      <div className="flex items-center gap-2">
                        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${docsOpen ? '' : '-rotate-90'}`} />
                        <FileText className="w-4 h-4 text-primary" />
                        <p className="text-sm font-semibold">Executed Documents</p>
                        <span className="text-xs text-muted-foreground">({executedDocs.length})</span>
                      </div>
                      {docsOpen && executedDocs.length > 0 && (
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={e => { e.stopPropagation(); executedDocs.forEach((doc, i) => { if (doc.authorization_file_id) setTimeout(() => fetchPortalFile(doc.authorization_file_id).then(b => downloadBlob(b, doc.authorization_filename || `executed-doc-${i+1}.pdf`)).catch(() => {}), i * 400); }); }}>
                          <Download className="w-3 h-3 mr-1" /> Download All
                        </Button>
                      )}
                    </div>
                    {docsOpen && (
                      executedDocs.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4 px-3">No executed documents</p>
                      ) : (
                        <div className="px-3 pb-3">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Consignor</TableHead>
                                <TableHead>Sale</TableHead>
                                <TableHead>Document</TableHead>
                                <TableHead>Executed</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {executedDocs.map(doc => (
                                <TableRow key={doc.deliverable_id}>
                                  <TableCell className="text-sm">{doc.consignor_name || '—'}</TableCell>
                                  <TableCell className="text-sm">{doc.sale_name || '—'}</TableCell>
                                  <TableCell className="text-sm font-medium">{doc.doc_label || 'Sale Authorization'}</TableCell>
                                  <TableCell className="text-sm">
                                    {doc.executed_at ? fmtDate(doc.executed_at) : '—'}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <div className="flex gap-1 justify-end">
                                      {doc.authorization_file_id && (
                                        <>
                                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Preview"
                                            onClick={() => openPdfPreview(() => fetchPortalFile(doc.authorization_file_id), doc.doc_label || 'Executed Document')}>
                                            <Eye className="w-3.5 h-3.5" />
                                          </Button>
                                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Download"
                                            onClick={() => fetchPortalFile(doc.authorization_file_id).then(b => downloadBlob(b, doc.authorization_filename || 'authorization-executed.pdf')).catch(() => toast.error('Failed to download'))}>
                                            <Download className="w-3.5 h-3.5" />
                                          </Button>
                                        </>
                                      )}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )
                    )}
                  </div>
                  );
                })()}

                {/* Completed Questionnaires */}
                {(() => {
                  const completedQ = portalQuestionnaires.filter(q => q.status === 'completed');
                  return (
                  <div className="border rounded">
                    <div className="flex justify-between items-center p-3 cursor-pointer hover:bg-muted/30" onClick={() => setQuestOpen(prev => !prev)}>
                      <div className="flex items-center gap-2">
                        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${questOpen ? '' : '-rotate-90'}`} />
                        <FileText className="w-4 h-4 text-primary" />
                        <p className="text-sm font-semibold">Completed Questionnaires</p>
                        <span className="text-xs text-muted-foreground">({completedQ.length})</span>
                      </div>
                      {questOpen && completedQ.length > 0 && (
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={e => { e.stopPropagation(); completedQ.forEach((q, i) => { if (q.questionnaire_file_id) setTimeout(() => fetchPortalFile(q.questionnaire_file_id).then(b => downloadBlob(b, q.questionnaire_filename || `questionnaire-${i+1}.pdf`)).catch(() => {}), i * 400); }); }}>
                          <Download className="w-3 h-3 mr-1" /> Download All
                        </Button>
                      )}
                    </div>
                    {questOpen && (
                      completedQ.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4 px-3">No completed questionnaires</p>
                      ) : (
                        <div className="px-3 pb-3">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Consignor</TableHead>
                                <TableHead>Sale</TableHead>
                                <TableHead>Horse</TableHead>
                                <TableHead>Hip #</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {completedQ.map(q => (
                                <TableRow key={q.deliverable_id}>
                                  <TableCell className="text-sm">{q.consignor_name || '—'}</TableCell>
                                  <TableCell className="text-sm">{q.sale_name || '—'}</TableCell>
                                  <TableCell className="text-sm font-medium">{q.horse_name || '—'}</TableCell>
                                  <TableCell className="text-sm">#{q.hip_number}</TableCell>
                                  <TableCell className="text-right">
                                    <div className="flex gap-1 justify-end">
                                      {q.questionnaire_file_id && (
                                        <>
                                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Preview PDF"
                                            onClick={() => openPdfPreview(() => fetchPortalFile(q.questionnaire_file_id), `${q.horse_name || 'Questionnaire'} — Completed`)}>
                                            <Eye className="w-3.5 h-3.5" />
                                          </Button>
                                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Download PDF"
                                            onClick={() => fetchPortalFile(q.questionnaire_file_id).then(b => downloadBlob(b, q.questionnaire_filename || 'questionnaire-completed.pdf')).catch(() => toast.error('Failed'))}>
                                            <Download className="w-3.5 h-3.5" />
                                          </Button>
                                        </>
                                      )}
                                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-primary" title="View system intake"
                                        onClick={() => setCompletingQuestionnaire(q.deliverable_id)}>
                                        <FileText className="w-3.5 h-3.5" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )
                    )}
                  </div>
                  );
                })()}

                {/* Published Reports */}
                <div className="border rounded">
                  <div className="flex justify-between items-center p-3 cursor-pointer hover:bg-muted/30" onClick={() => setReportsOpen && setReportsOpen(!reportsOpen)}>
                    <div className="flex items-center gap-2">
                      <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${reportsOpen ? '' : '-rotate-90'}`} />
                      <FileText className="w-4 h-4 text-primary" />
                      <p className="text-sm font-semibold">Published Reports</p>
                      <span className="text-xs text-muted-foreground">({publishedReports.length})</span>
                    </div>
                    {reportsOpen && publishedReports.length > 0 && (
                      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={e => { e.stopPropagation(); publishedReports.forEach((r, i) => { if (r.saved_report_id) setTimeout(() => fetchReportPdf(r.saved_report_id).then(b => downloadBlob(b, `${(r.report_name||'Report').replace(/\s+/g,'-')}.pdf`)).catch(() => {}), i * 600); }); }}>
                        <Download className="w-3 h-3 mr-1" /> Download All
                      </Button>
                    )}
                  </div>
                  {reportsOpen && (
                    publishedReports.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4 px-3">No published reports</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-muted/30">
                              <TableHead className="text-xs">Consignor</TableHead>
                              <TableHead className="text-xs">Sale</TableHead>
                              <TableHead className="text-xs">Report Type</TableHead>
                              <TableHead className="text-xs">Date</TableHead>
                              <TableHead className="text-xs text-right">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {publishedReports.map(r => (
                              <TableRow key={r.id || r.saved_report_id} className="hover:bg-muted/20">
                                <TableCell className="text-sm font-medium">{r.consignor || '—'}</TableCell>
                                <TableCell className="text-sm">{r.sale_name || '—'}</TableCell>
                                <TableCell className="text-sm">
                                  {r.report_type === 'daily-sale-updates' ? 'Daily Sale Update'
                                   : r.report_type === 'net-proceeds' ? 'Net Proceeds'
                                   : r.report_type === 'sales-performance' ? 'Sales Performance'
                                   : r.report_name || r.report_type || '—'}
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                  {r.published_at ? fmtDate(r.published_at) : '—'}
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex items-center gap-1 justify-end">
                                    {r.saved_report_id && (
                                      <>
                                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-primary"
                                          title="Preview report"
                                          onClick={() => openPdfPreview(() => fetchReportPdf(r.saved_report_id), r.report_name || 'Report')}
                                          data-testid={`view-report-${r.saved_report_id}`}
                                        >
                                          <Eye className="w-3.5 h-3.5" />
                                        </Button>
                                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-primary"
                                          title="Download PDF"
                                          onClick={() => fetchReportPdf(r.saved_report_id).then(b => downloadBlob(b, `${(r.report_name||'Report').replace(/\s+/g,'-')}.pdf`)).catch(() => toast.error('Failed to download PDF'))}
                                          data-testid={`download-report-${r.saved_report_id}`}
                                        >
                                          <Download className="w-3.5 h-3.5" />
                                        </Button>
                                      </>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-white py-3 mt-auto">
        <div className="px-6 text-center text-xs text-muted-foreground">
          a <span className="font-semibold">Barn Door Software, LLC</span> product
        </div>
      </footer>

      {/* Link Consignor Dialog */}
      <Dialog open={linkDialog} onOpenChange={setLinkDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Link to a Connection</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">Enter the access key provided by your Consignor, Farm or Syndicate.</p>
            <div className="flex items-center gap-2">
              <Key className="w-5 h-5 text-muted-foreground shrink-0" />
              <Input
                placeholder="Enter access key..."
                value={linkKey}
                onChange={async (e) => {
                  const val = e.target.value;
                  setLinkKey(val);
                  setLinkKeyResult(null);
                  if (val.trim().length >= 8) {
                    try {
                      const res = await api.get(`/owner-portal/verify-key?key=${val.trim()}`);
                      setLinkKeyResult(res.data);
                    } catch { setLinkKeyResult(null); }
                  }
                }}
                data-testid="link-key-input"
              />
            </div>
            {linkKeyResult && (
              linkKeyResult.valid ? (
                <div className="p-3 bg-green-50 border border-green-200 rounded flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-600 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-green-800">{linkKeyResult.tenant_name}</p>
                    <p className="text-xs text-green-700">{linkKeyResult.entity_name}</p>
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-red-50 border border-red-200 rounded">
                  <p className="text-sm text-red-700">Invalid access key</p>
                </div>
              )
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialog(false)}>Cancel</Button>
            <Button onClick={handleLinkConsignor} disabled={linking || !linkKey.trim()} className="bg-[#346B4E] hover:bg-[#2a5740] text-white" data-testid="link-submit-btn">
              {linking ? 'Linking...' : 'Request Access'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Consignor Detail Dialog */}
      <Dialog open={!!consignorDetail} onOpenChange={(v) => { if (!v) setConsignorDetail(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{consignorDetail?.name}</DialogTitle>
          </DialogHeader>
          {(() => {
            const linkedEntity = entities.find(e => e.tenant_id === consignorDetail?.id);
            return (
          <div className="space-y-3 py-2">
            {consignorDetail?.logo_url && (
              <img src={makeLogoUrl(consignorDetail.logo_url)} alt={consignorDetail?.name} className="w-20 h-20 object-contain mx-auto"
                onError={e => { e.currentTarget.style.display = 'none'; }} />
            )}
            <div className="space-y-2 text-sm">
              {consignorDetail?.email && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Email</span>
                  <span>{consignorDetail.email}</span>
                </div>
              )}
              {consignorDetail?.phone && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Phone</span>
                  <span>{consignorDetail.phone}</span>
                </div>
              )}
              {consignorDetail?.address && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Address</span>
                  <span>{consignorDetail.address}</span>
                </div>
              )}
              {linkedEntity?.account_manager_name && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Account Manager</span>
                  <span>{linkedEntity.account_manager_name}</span>
                </div>
              )}
              {linkedEntity?.account_admin_name && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Account Admin</span>
                  <span>{linkedEntity.account_admin_name}</span>
                </div>
              )}
            </div>
          </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConsignorDetail(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sale Opportunity Detail Dialog (also used for Final Sale Reports) */}
      <Dialog open={!!saleOppDetail || !!finalReportDetail || saleOppLoading} onOpenChange={(v) => { if (!v) { setSaleOppDetail(null); setFinalReportDetail(null); setSaleOppLoading(false); setSaleOppMeta(null); } }}>
        <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-y-auto overflow-x-hidden w-full" style={{ maxWidth: 'min(56rem, calc(100vw - 1rem))' }}>
          {saleOppLoading ? (
            <div className="flex justify-center py-12"><Skeleton className="h-8 w-48" /></div>
          ) : (saleOppDetail || finalReportDetail) && (() => {
            const d = saleOppDetail || finalReportDetail;
            const m = saleOppMeta;
            const isFinalReport = !!finalReportDetail;
            return (
              <>
                <DialogHeader className="min-w-0">
                  <DialogTitle className="font-serif text-xl min-w-0 truncate">{m?.horse_name || 'Sale Opportunity'}</DialogTitle>
                  <p className="text-sm text-muted-foreground truncate">{m?.sale_name} • {m?.sale_company} • Hip #{m?.hip_number || 'TBD'}</p>
                  {m?.sale_start_date && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                      <Calendar className="w-3 h-3 shrink-0" />
                      {fmtDateShort(m.sale_start_date)}
                      {m.sale_end_date && ` – ${fmtDate(m.sale_end_date)}`}
                    </p>
                  )}
                  {m?.is_undertack_show && (m?.under_tack_start_date || m?.under_tack_end_date) && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                      <Timer className="w-3 h-3 shrink-0" />
                      Undertack:{' '}
                      {m.under_tack_start_date && fmtDateShort(m.under_tack_start_date)}
                      {m.under_tack_start_date && m.under_tack_end_date && ' – '}
                      {m.under_tack_end_date && fmtDateShort(m.under_tack_end_date)}
                    </p>
                  )}
                </DialogHeader>

                <div className="space-y-5 py-2" style={{ width: '100%', minWidth: 0, overflowX: 'hidden' }}>
                  {/* Breeze Times card — above stats, only when undertack show and data exists */}
                  {m?.is_undertack_show && saleOppBreezeTime && (saleOppBreezeTime.ut_time || saleOppBreezeTime.ut_distance) && (
                    <Card>
                      <CardContent className="p-3 flex items-center gap-3">
                        <Timer className="w-5 h-5 text-primary shrink-0" />
                        <div>
                          <p className="text-[10px] text-muted-foreground">Breeze Time</p>
                          <p className="text-sm font-medium">
                            {[saleOppBreezeTime.ut_time, saleOppBreezeTime.ut_distance].filter(Boolean).join(' · ')}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Stats Cards — 2×2 grid, each cell shrinks to fit */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.5rem' }}>
                    {[
                      { icon: Eye, label: 'Views Today',    val: `${d.views_today} of ${d.views_today_requested} requested` },
                      { icon: Eye, label: 'Total Views',    val: `${d.total_views_completed} of ${d.total_views_requested} requested` },
                      { icon: Stethoscope, label: 'Vetting Today', val: String(d.vetting_today) },
                      { icon: Stethoscope, label: 'Total Vetting', val: String(d.total_vetting) },
                    ].map(({ icon: Icon, label, val }) => (
                      <div key={label} style={{ minWidth: 0, overflow: 'hidden', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '0.625rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <Icon style={{ width: '1.125rem', height: '1.125rem', color: 'hsl(var(--primary))', flexShrink: 0 }} />
                          <div style={{ minWidth: 0 }}>
                            <p style={{ fontSize: '0.625rem', color: 'var(--muted-foreground)', margin: 0 }}>{label}</p>
                            <p style={{ fontSize: '0.8125rem', fontWeight: 700, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{val}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Views Table — scrollable within the card */}
                  <Card style={{ minWidth: 0, overflow: 'hidden' }}>
                    <CardHeader className="py-3 px-4">
                      <CardTitle className="text-sm font-semibold">Views ({d.viewings.length})</CardTitle>
                    </CardHeader>
                    <CardContent className="px-4 py-0 pb-4">
                      {d.viewings.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">No views recorded yet</p>
                      ) : (
                        <div style={{ overflowX: 'auto', width: '100%' }}>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Viewer</TableHead>
                              <TableHead>Created</TableHead>
                              <TableHead>Card Views</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead>Notes</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {d.viewings.map((v, i) => (
                              <TableRow key={i}>
                                <TableCell className="text-sm font-medium">{v.viewer_name || '-'}</TableCell>
                                <TableCell className="text-sm">{v.created_at ? new Date(v.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '-'}</TableCell>
                                <TableCell className="text-sm">
                                  1 of {v.card_total_viewings}
                                  {v.card_type && v.card_type !== 'individual' && (
                                    <Badge variant="outline" className="text-[10px] ml-1 pointer-events-none capitalize">{v.card_type.replace(/_/g, ' ')}</Badge>
                                  )}
                                </TableCell>
                                <TableCell className="text-sm">
                                  {v.status === 'completed' ? (
                                    <span>
                                      <Badge className="bg-green-100 text-green-700 text-xs pointer-events-none">Done</Badge>
                                      {v.updated_at && <span className="text-xs text-muted-foreground ml-1">({new Date(v.updated_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })})</span>}
                                    </span>
                                  ) : (
                                    <Badge className="bg-gray-100 text-gray-700 text-xs pointer-events-none">Pending</Badge>
                                  )}
                                </TableCell>
                                <TableCell className="text-sm">
                                  {v.liked && <Heart className="w-3 h-3 text-red-500 fill-red-500 inline mr-1" />}
                                  {v.viewed_vet_report && <FileText className="w-3 h-3 text-blue-500 inline mr-1" />}
                                  {v.notes || (!v.liked && !v.viewed_vet_report ? '-' : '')}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Vetting Procedures */}
                  <Card>
                    <CardHeader className="py-3 px-4">
                      <CardTitle className="text-sm font-semibold">Vetting Procedures ({d.vetting_procedures.length})</CardTitle>
                    </CardHeader>
                    <CardContent className="px-4 py-0 pb-4">
                      {d.vetting_procedures.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">No vetting procedures recorded yet</p>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Vet</TableHead>
                              <TableHead>Client</TableHead>
                              <TableHead>Date / Time</TableHead>
                              <TableHead>Notes</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {d.vetting_procedures.map((vp, i) => (
                              <TableRow key={i}>
                                <TableCell className="text-sm">
                                  <p className="font-medium">{vp.vet_name || '-'}</p>
                                  {vp.vet_practice && <p className="text-xs text-muted-foreground">{vp.vet_practice}</p>}
                                </TableCell>
                                <TableCell className="text-sm">{vp.client_name || '-'}</TableCell>
                                <TableCell className="text-sm">{vp.procedure_date_time ? new Date(vp.procedure_date_time).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '-'}</TableCell>
                                <TableCell className="text-sm capitalize">
                                  {vp.procedure?.replace(/_/g, ' ') || '-'}
                                  {vp.viewed_vet_report && <FileText className="w-3 h-3 text-blue-500 inline ml-1" />}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </CardContent>
                  </Card>

                  {/* Sale Results - only if result is not pending */}
                  {d.result && d.result !== 'pending' && (
                    <Card>
                      <CardHeader className="py-3 px-4">
                        <CardTitle className="text-sm font-semibold">Sale Results</CardTitle>
                      </CardHeader>
                      <CardContent className="px-4 py-0 pb-4 space-y-4">
                        {/* Reserve & Net Proceeds */}
                        <div className="flex gap-4">
                          <div className="p-3 rounded flex items-center gap-2" style={{ backgroundColor: 'rgba(234, 179, 8, 0.1)' }}>
                            <DollarSign className="w-5 h-5 text-yellow-600" />
                            <div>
                              <p className="text-xs text-muted-foreground">Reserve Price</p>
                              <p className="text-lg font-bold">{d.reserve_price ? formatCurrency(d.reserve_price) : '-'}</p>
                            </div>
                          </div>
                          <div className="p-3 rounded flex items-center gap-2" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
                            <DollarSign className="w-5 h-5 text-primary" />
                            <div>
                              <p className="text-xs text-muted-foreground">Net Proceeds</p>
                              <p className="text-lg font-bold text-primary">{d.net_proceeds ? formatCurrency(d.net_proceeds) : '-'}</p>
                            </div>
                          </div>
                        </div>

                        <hr className="border-border" />

                        {/* Result Details */}
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Result</p>
                          <Badge className={
                            d.result === 'sold' ? 'bg-green-100 text-green-700 pointer-events-none' :
                            d.result === 'rna' ? 'bg-red-100 text-red-700 pointer-events-none' :
                            'bg-gray-100 text-gray-700 pointer-events-none'
                          }>{d.result === 'sold' ? 'Sold' : d.result === 'rna' ? 'RNA' : d.result?.replace(/_/g, ' ')}</Badge>
                        </div>

                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-xs text-muted-foreground">Sale Price</p>
                            <p className="font-medium text-primary">{d.sale_price ? formatCurrency(d.sale_price) : '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Signed Ticket Buyer</p>
                            <p className="font-medium">{d.signed_ticket_as || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Payment Method</p>
                            <p className="font-medium capitalize">{d.payment_method?.replace(/_/g, ' ') || '-'}</p>
                          </div>
                        </div>

                        {/* Sale Company Fees */}
                        <div>
                          <p className="text-sm font-semibold mb-2">Sale Company Fees</p>
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <p className="text-xs text-muted-foreground">Charge Back</p>
                              <p className="font-medium">{d.sale_company_chargeback ? formatCurrency(d.sale_company_chargeback) : '-'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Entry Fee</p>
                              <p className="font-medium">{d.sale_company_entry_fee ? formatCurrency(d.sale_company_entry_fee) : '-'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Commission</p>
                              <p className="font-medium">{d.sale_company_commission ? formatCurrency(d.sale_company_commission) : '-'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Liens</p>
                              <p className="font-medium">{d.sale_company_lien ? formatCurrency(d.sale_company_lien) : '-'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Other</p>
                              <p className="font-medium">{d.sale_company_other_amount ? formatCurrency(d.sale_company_other_amount) : '-'}</p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Buyer - only for Final Sale Reports */}
                  {isFinalReport && d.buyers && d.buyers.length > 0 && (
                    <Card>
                      <CardHeader className="py-3 px-4">
                        <CardTitle className="text-sm font-semibold">Buyer(s)</CardTitle>
                      </CardHeader>
                      <CardContent className="px-4 py-0 pb-4">
                        <div className="space-y-2">
                          {d.buyers.map((b, i) => (
                            <div key={i} className="flex items-center gap-3 p-2 border rounded">
                              <div className="flex-1">
                                <p className="text-sm font-medium">{b.system_name}</p>
                                <p className="text-xs text-muted-foreground capitalize">
                                  {b.person_industry_role?.replace(/_/g, ' ') || b.organization_industry_role?.replace(/_/g, ' ') || ''}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Photos & Files - only for Final Sale Reports */}
                  {isFinalReport && (d.files || []).length > 0 && (
                    <Card>
                      <CardHeader className="py-3 px-4">
                        <CardTitle className="text-sm font-semibold">Photos & Files</CardTitle>
                      </CardHeader>
                      <CardContent className="px-4 py-0 pb-4">
                        <div className="space-y-1">
                          {(d.files || []).map((f) => (
                            <div key={f.id}>
                              <div className="flex items-center gap-3 p-2 rounded hover:bg-muted/50">
                                {f.mime_type?.startsWith('image/') ? <Image className="w-5 h-5 text-blue-500 shrink-0" /> :
                                 f.mime_type?.startsWith('video/') ? <Film className="w-5 h-5 text-purple-500 shrink-0" /> :
                                 f.mime_type === 'application/pdf' ? <FileText className="w-5 h-5 text-red-500 shrink-0" /> :
                                 <FileText className="w-5 h-5 text-muted-foreground shrink-0" />}
                                <span className="text-sm flex-1 truncate">
                                  {f.deliverable_label ? `${f.deliverable_label} - ` : ''}{f.description || f.original_filename}
                                </span>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="View" onClick={async () => {
                                  if (reportFilePreview === f.id) { setReportFilePreview(null); return; }
                                  setReportFilePreview(f.id);
                                  if (!reportFileBlobUrls[f.id]) {
                                    try {
                                      const token = localStorage.getItem('token');
                                      const endpoint = f.is_deliverable_file
                                        ? `${window.location.origin}/api/owner-portal/deliverable-file/${f.id}`
                                        : `${window.location.origin}/api/owner-portal/file/${f.id}`;
                                      const res = await fetch(endpoint, { headers: { Authorization: `Bearer ${token}` } });
                                      if (res.ok) {
                                        const blob = await res.blob();
                                        setReportFileBlobUrls(prev => ({ ...prev, [f.id]: URL.createObjectURL(blob) }));
                                      }
                                    } catch {}
                                  }
                                }}>
                                  <Eye className="w-3.5 h-3.5" />
                                </Button>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Download" onClick={async () => {
                                  try {
                                    const token = localStorage.getItem('token');
                                    const endpoint = f.is_deliverable_file
                                      ? `${window.location.origin}/api/owner-portal/deliverable-file/${f.id}`
                                      : `${window.location.origin}/api/owner-portal/file/${f.id}`;
                                    const res = await fetch(endpoint, { headers: { Authorization: `Bearer ${token}` } });
                                    if (res.ok) { const blob = await res.blob(); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = f.original_filename || 'file'; a.click(); URL.revokeObjectURL(url); }
                                  } catch {}
                                }}>
                                  <Download className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                              {reportFilePreview === f.id && reportFileBlobUrls[f.id] && (
                                <div className="p-3 bg-muted/30 rounded-lg mt-1 mb-2">
                                  {f.mime_type?.startsWith('image/') ? (
                                    <img src={reportFileBlobUrls[f.id]} alt={f.description || f.original_filename} className="max-w-full max-h-[400px] object-contain rounded mx-auto" />
                                  ) : f.mime_type === 'application/pdf' ? (
                                    <iframe src={reportFileBlobUrls[f.id]} className="w-full h-[400px] border rounded" title={f.original_filename} />
                                  ) : f.mime_type?.startsWith('video/') ? (
                                    <video src={reportFileBlobUrls[f.id]} controls autoPlay className="max-w-full max-h-[400px] mx-auto rounded" />
                                  ) : (
                                    <p className="text-sm text-muted-foreground text-center py-4">Preview not available</p>
                                  )}
                                </div>
                              )}
                              {reportFilePreview === f.id && !reportFileBlobUrls[f.id] && (
                                <div className="p-3 text-center"><Skeleton className="h-32 w-full" /></div>
                              )}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => { setSaleOppDetail(null); setFinalReportDetail(null); setSaleOppMeta(null); }}>Close</Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* PDF Preview Dialog */}
      <Dialog open={!!pdfPreview} onOpenChange={(v) => { if (!v) { if (pdfPreview && pdfPreview !== 'loading') URL.revokeObjectURL(pdfPreview); setPdfPreview(null); setPdfLoading(false); setPdfPreviewType('pdf'); } }}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden w-full p-4" style={{ maxWidth: 'min(56rem, calc(100vw - 1rem))' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {pdfPreviewTitle}
              {pdfLoading && <span className="w-3.5 h-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin inline-block" />}
            </DialogTitle>
          </DialogHeader>
          {!pdfLoading && pdfPreview && pdfPreview !== 'loading'
            ? pdfPreviewType === 'image'
              ? <div className="flex justify-center">
                  <img src={pdfPreview} alt={pdfPreviewTitle} className="max-w-full max-h-[70vh] object-contain rounded" />
                </div>
              : <PDFViewer src={pdfPreview} />
            : <div className="h-48" />}
          <DialogFooter>
            <Button variant="outline" onClick={() => { if (pdfPreview && pdfPreview !== 'loading') URL.revokeObjectURL(pdfPreview); setPdfPreview(null); setPdfLoading(false); setPdfPreviewType('pdf'); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Horse Detail Dialog */}
      <Dialog open={!!horseDetail || horseDetailLoading} onOpenChange={(v) => { if (!v) { setHorseDetail(null); setHorseDetailLoading(false); } }}>
        <DialogContent
          className="sm:max-w-4xl w-full p-0"
          style={{ maxWidth: 'min(56rem, calc(100vw - 1rem))', overflow: 'hidden' }}
        >
          {/* Vertical scroll lives here — overflow:hidden on DialogContent clips width,
              overflow-y:auto here handles vertical. Child overflow-x:auto tables still scroll. */}
          <div style={{ overflowY: 'auto', maxHeight: '85vh', padding: '1.5rem' }}>
          {horseDetailLoading ? (
            <div className="flex justify-center py-12"><Skeleton className="h-8 w-48" /></div>
          ) : horseDetail && (() => {
            const h = horseDetail.horse;
            const owns = horseDetail.ownership_records || [];
            const confNotes = (horseDetail.conformation_notes || [])
                              .slice()
                              .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
            const covers = horseDetail.cover_records || [];
            const produces = horseDetail.produce_records || [];
            const races = horseDetail.race_class_records || [];
            const files = horseDetail.files || [];
            const saleOpps = horseDetail.sale_opportunities || [];
            const farmfolioCare = (horseDetail.farmfolio_care || []);
            const currentCover = covers.find(c => c.is_current_cover);

            return (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-4">
                    {h.main_photo_url ? (
                      <img src={h.main_photo_url} alt={h.system_name} className="w-16 h-16 rounded-lg object-cover shrink-0" />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center shrink-0">
                        <HorseshoeIcon className="w-8 h-8 text-muted-foreground/30" />
                      </div>
                    )}
                    <div>
                      <DialogTitle className="font-serif text-2xl">{h.system_name}</DialogTitle>
                      <p className="text-sm text-muted-foreground">
                        {[h.age, h.gender, h.color?.replace(/_/g, ' ')].filter(Boolean).join(' • ')}
                      </p>
                    </div>
                  </div>
                </DialogHeader>

                <div className="space-y-5 py-2">
                  {/* Basic Details */}
                  <div className="border rounded-lg p-4">
                    <h3 className="text-sm font-semibold mb-2">Basic Details</h3>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                      <div className="flex justify-between"><span className="text-muted-foreground">Sire</span><span>{h.sire_name || '-'}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Dam</span><span>{h.dam_name || '-'}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Birthdate</span><span>{fmtDate(h.birthdate)}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Area Foaled</span><span>{h.area_foaled || '-'}</span></div>
                    </div>
                  </div>

                  {/* Current Ownership */}
                  {owns.length > 0 && (
                    <div className="border rounded-lg p-4" style={{ overflow: 'hidden' }}>
                      <h3 className="text-sm font-semibold mb-2">Current Ownership</h3>
                      <div style={{ overflowX: 'auto', width: '100%' }}>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Owner</TableHead>
                            <TableHead>Ownership %</TableHead>
                            <TableHead>Primary</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {owns.map((o) => (
                            <TableRow key={o.id}>
                              <TableCell className="text-sm">{o.entity_name || '-'}</TableCell>
                              <TableCell className="text-sm">{o.ownership_percent}%</TableCell>
                              <TableCell className="text-sm">{o.is_primary_owner ? 'Yes' : '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      </div>
                    </div>
                  )}

                  {/* Notes (published) */}
                  {confNotes.length > 0 && (() => {
                    const NOTE_TYPES = [
                      { value: 'all',                  label: 'All' },
                      { value: 'conformation_note',     label: 'Conformation' },
                      { value: 'vet_procedure_note',    label: 'Vet Procedure' },
                      { value: 'layup_note',           label: 'Layup' },
                      { value: 'sales_inspection_note', label: 'Sale Inspection' },
                      { value: 'other_note',           label: 'Other' },
                    ];
                    // Only show types that actually appear in the notes
                    const presentTypes = new Set(confNotes.map(n => n.note_type || 'conformation_note'));
                    const visibleFilters = NOTE_TYPES.filter(t => t.value === 'all' || presentTypes.has(t.value));
                    return (
                      <NotesFilterSection confNotes={confNotes} visibleFilters={visibleFilters}
                        setPdfPreview={setPdfPreview} setPdfPreviewTitle={setPdfPreviewTitle} setPdfPreviewType={setPdfPreviewType} />
                    );
                  })()}

                  {/* Care Updates — published FarmFolio entries, between Notes and Breeding */}
                  {farmfolioCare.length > 0 && <CareUpdatesSection farmfolioCare={farmfolioCare} />}

                  {/* Current Cover Status & Cover History */}
                  {covers.length > 0 && (
                    <div className="border rounded-lg p-4">
                      <h3 className="text-sm font-semibold mb-2">Breeding</h3>
                      {currentCover && (
                        <div className="p-3 border rounded text-sm mb-2" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
                          <p className="font-medium">Current Cover: {currentCover.covering_sire}</p>
                          <p className="text-xs text-muted-foreground">Date: {currentCover.cover_date ? fmtDate(currentCover.cover_date) : '-'} • Outcome: {currentCover.outcome || 'Pending'}</p>
                        </div>
                      )}
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Covering Sire</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead>Outcome</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {covers.map(c => (
                            <TableRow key={c.id}>
                              <TableCell className="text-sm">{c.covering_sire || '-'}</TableCell>
                              <TableCell className="text-sm">{c.cover_date ? fmtDate(c.cover_date) : '-'}</TableCell>
                              <TableCell className="text-sm capitalize">{c.outcome || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}

                  {/* Produce Records */}
                  {produces.length > 0 && (
                    <div className="border rounded-lg p-4">
                      <h3 className="text-sm font-semibold mb-2">Produce Records</h3>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Year</TableHead>
                            <TableHead>Foal Name</TableHead>
                            <TableHead>Sire</TableHead>
                            <TableHead>Sex</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {produces.map(p => (
                            <TableRow key={p.id}>
                              <TableCell className="text-sm">{p.foal_year || '-'}</TableCell>
                              <TableCell className="text-sm">{p.foal_name || '-'}</TableCell>
                              <TableCell className="text-sm">{p.sire_name || '-'}</TableCell>
                              <TableCell className="text-sm">{p.foal_sex || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}

                  {/* Racing */}
                  {races.length > 0 && (
                    <div className="border rounded-lg p-4" style={{ overflow: 'hidden' }}>
                      <h3 className="text-sm font-semibold mb-2">Race Records</h3>
                      <div style={{ overflowX: 'auto', width: '100%' }}>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Race</TableHead>
                            <TableHead>Class</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead>Result</TableHead>
                            <TableHead>Purse</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {races.map(r => (
                            <TableRow key={r.id}>
                              <TableCell className="text-sm font-medium">{r.race_name || '-'}</TableCell>
                              <TableCell className="text-sm capitalize">{r.race_class?.replace(/_/g, ' ') || '-'}</TableCell>
                              <TableCell className="text-sm">{r.race_date ? fmtDate(r.race_date) : '-'}</TableCell>
                              <TableCell className="text-sm">{r.race_result || '-'}</TableCell>
                              <TableCell className="text-sm">{r.race_purse ? `$${r.race_purse.toLocaleString()}` : '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      </div>
                    </div>
                  )}

                  {/* Published Files */}
                  {files.length > 0 && (
                    <div className="border rounded-lg p-4">
                      <h3 className="text-sm font-semibold mb-2">Photos & Files</h3>
                      <div className="space-y-1">
                        {files.map(f => (
                          <div key={f.id}>
                            <div
                              className="flex items-center gap-3 p-2 rounded hover:bg-muted/50 cursor-pointer transition-colors"
                              onClick={() => openHorseFile(f.id)}
                            >
                              {f.mime_type?.startsWith('image/') && horseFileBlobUrls[f.id] ? (
                                <img src={horseFileBlobUrls[f.id]} alt="" className="w-8 h-8 rounded object-cover shrink-0" />
                              ) : f.mime_type?.startsWith('image/') ? (
                                <Image className="w-5 h-5 text-blue-500 shrink-0" />
                              ) : f.mime_type?.startsWith('video/') ? (
                                <Film className="w-5 h-5 text-purple-500 shrink-0" />
                              ) : f.mime_type === 'application/pdf' ? (
                                <FileText className="w-5 h-5 text-red-500 shrink-0" />
                              ) : (
                                <FileText className="w-5 h-5 text-muted-foreground shrink-0" />
                              )}
                              <span className="text-sm flex-1 truncate">{f.description || f.original_filename}</span>
                              <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${horseFilePreview === f.id ? 'rotate-90' : ''}`} />
                            </div>
                            {horseFilePreview === f.id && horseFileBlobUrls[f.id] && (() => {
                              const effectiveMime = horseFileMimeTypes[f.id] || f.mime_type || '';
                              return (
                                <div className="p-3 bg-muted/30 rounded-lg mt-1 mb-2">
                                  {effectiveMime.startsWith('image/') ? (
                                    <img src={horseFileBlobUrls[f.id]} alt={f.description || f.original_filename} className="max-w-full max-h-[400px] object-contain rounded mx-auto" />
                                  ) : effectiveMime === 'application/pdf' ? (
                                    <PDFViewer src={horseFileBlobUrls[f.id]} />
                                  ) : effectiveMime.startsWith('video/') ? (
                                    <video src={horseFileBlobUrls[f.id]} controls autoPlay className="max-w-full max-h-[400px] mx-auto rounded" />
                                  ) : effectiveMime.startsWith('text/') ? (
                                    <pre className="text-xs text-muted-foreground whitespace-pre-wrap break-words max-h-[300px] overflow-y-auto p-2 bg-background rounded border">
                                      {horseFileTextContent[f.id] || '(empty file)'}
                                    </pre>
                                  ) : (
                                    <div className="flex flex-col items-center gap-3 py-4">
                                      <p className="text-sm text-muted-foreground">Preview not available for this file type.</p>
                                      <a href={horseFileBlobUrls[f.id]} download={f.description || f.original_filename || 'file'} className="text-sm underline text-primary">
                                        Download file
                                      </a>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                            {horseFilePreview === f.id && !horseFileBlobUrls[f.id] && (
                              <div className="p-3 text-center"><Skeleton className="h-32 w-full" /></div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Sale Opportunities */}
                  {saleOpps.length > 0 && (
                    <div className="border rounded-lg p-4" style={{ overflow: 'hidden' }}>
                      <h3 className="text-sm font-semibold mb-2">Sale Opportunities</h3>
                      <div style={{ overflowX: 'auto', width: '100%' }}>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Sale</TableHead>
                            <TableHead>Presale Stage</TableHead>
                            <TableHead>Hip #</TableHead>
                            <TableHead>Result</TableHead>
                            <TableHead>Sale Price</TableHead>
                            <TableHead>Date Created</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {saleOpps.map((opp) => {
                            const stageBadges = {
                              presale_create: { label: 'Potential Sale Entry', cls: 'bg-gray-100 text-gray-700' },
                              presale_confirm: { label: 'Confirmed Sale Entry', cls: 'bg-blue-100 text-blue-700' },
                              presale_deliverables: { label: 'Tracking Deliverables', cls: 'bg-yellow-100 text-yellow-700' },
                              presale_ready: { label: 'Ready for Sale', cls: 'bg-green-100 text-green-700' },
                              live_sale: { label: 'Live Sale', cls: 'bg-primary text-white' },
                              post_sale: { label: 'Complete', cls: 'bg-muted text-muted-foreground' },
                            };
                            const resultBadges = {
                              sold: { label: 'SOLD', cls: 'bg-green-600 text-white' },
                              rna: { label: 'RNA', cls: 'bg-red-100 text-red-700' },
                              withdrawn: { label: 'Withdrawn', cls: 'bg-gray-100 text-gray-700' },
                              scratched: { label: 'Scratched', cls: 'bg-gray-100 text-gray-700' },
                            };
                            const sb = stageBadges[opp.stage];
                            const rb = resultBadges[opp.result];
                            return (
                              <TableRow key={opp.id}>
                                <TableCell className="text-sm">
                                  <div>
                                    <p className="font-medium">{opp.sale_name || '-'}</p>
                                    <p className="text-xs text-muted-foreground">{[opp.sale_company, opp.sale_year].filter(Boolean).join(' • ')}</p>
                                  </div>
                                </TableCell>
                                <TableCell>{sb ? <Badge className={`text-xs pointer-events-none ${sb.cls}`}>{sb.label}</Badge> : <span className="text-sm">-</span>}</TableCell>
                                <TableCell className="text-sm">#{opp.hip_number || 'TBD'}</TableCell>
                                <TableCell>{rb ? <Badge className={`text-xs pointer-events-none ${rb.cls}`}>{rb.label}</Badge> : <span className="text-sm">-</span>}</TableCell>
                                <TableCell className="text-sm font-medium">{opp.sale_price ? formatCurrency(opp.sale_price) : '-'}</TableCell>
                                <TableCell className="text-sm">{opp.created_at ? fmtDate(opp.created_at) : '-'}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                      </div>
                    </div>
                  )}
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setHorseDetail(null)}>Close</Button>
                </DialogFooter>
              </>
            );
          })()}
          </div>
        </DialogContent>
      </Dialog>

      {completingQuestionnaire && (
        <QuestionnaireCompletionDialog
          open={!!completingQuestionnaire}
          onClose={(done) => {
            setCompletingQuestionnaire(null);
            if (done) invalidateQuestionnaires();
          }}
          deliverableId={completingQuestionnaire}
          onCompleted={() => invalidateQuestionnaires()}
        />
      )}

      {/* Hidden file input for questionnaire upload */}
      <input
        ref={questUploadRef}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
        className="hidden"
        onChange={handleQuestionnaireUpload}
      />
    </div>
  );
};

export default OwnerPortalPage;
